import React from "react";
import Section from "./Section";
import TabbedSection from "./TabbedSection";
import SelectionPane from "./SelectionPane";
import ViewErrorBoundary from "./ViewErrorBoundary";
import Menu from "./Menu";
import MassChangeBar from "./MassChangeBar";
import Mousetrap from "@timeedit/mousetrap";
import _ from "underscore";
import { Tutorial } from "../lib/Tutorial";
import TemplateKind from "../models/TemplateKind";
import Language from "../lib/Language";
import Tutorials from "../lib/Tutorials";
import AboutDialog from "./About";
import ObjectDefinition from "./ObjectDefinition";
import CommandBar from "./CommandBar";
import { TOPBAR_BUTTON_ICON_HEIGHT } from "../lib/UIConstants";
import ResizeableComponent, { TResizableComponentStateAndProps } from "../lib/ResizeableComponent";
import ShortcutOverlay from "./ShortcutOverlay";
import ModalDialog from "./ModalDialog";
import FloatingWindow, { TFloatingWindowEntry, TFloatingWindowOptions } from "./FloatingWindow";
import ReservationStatus from "../lib/ReservationStatus";
import Log from "../lib/Log";
import Experiment from "../models/Experiment";
import ExceptionEditor from "./ExceptionEditor";
import { TimeConstants } from "../lib/TimeConstants";
import ExamConstants from "../lib/ExamConstants";
import { Scratchpad, ScratchpadObject, SourceType } from "../models/Scratchpad";
// Webpack alias for @resouces
// eslint-disable-next-line import/no-unresolved
import "@resources/sass/bundle.sass";

const { EXAM_MODE, EXAM_NOT_AUTHORIZED, EXAM_NO_ORG_ACCESS, EXAM_NOT_FOUND } = ExamConstants;

import { SimpleDateFormat, MillenniumDate, MillenniumDateTime } from "@timeedit/millennium-time";
import ActivityUtils, { TrackVariation } from "../lib/ActivityUtils";
import { toSearchCriteriaFromObjects } from "../lib/FilterUtils";
import { hasPermissions } from "@timeedit/types/lib/utils/permissionCalculator";

import { LDClient, LDFlagSet, withLDConsumer } from "launchdarkly-react-client-sdk";
import { OPERATION_TYPES, REQUEST_TYPES } from "./preferences/PrefsCoreAPI";
import API from "../lib/TimeEditAPI";
import { Macros } from "../models/Macros";
import { Menu as MenuModel } from "../models/Menu";
import { PrimaryFieldManager } from "../models/PrimaryFieldManager";
import ObjectNameCache from "../lib/ObjectNameCache";
import { McFluffy } from "../models/McFluffy";
import { User } from "../models/User";
import { View } from "../models/View";
import { TimeEdit } from "../lib/TimeEdit";
import { Calendar } from "../models/Calendar";
import { Selection } from "../models/Selection";
import { OrderSearch } from "../models/OrderSearch";
import { ReservationSearch } from "../models/ReservationSearch";
import { StaticReservations } from "../models/StaticReservations";
import { Section as SectionModel, TComponentType } from "../models/Section";
import { ExamData } from "../models/ExamData";
import { MemberData } from "../models/MemberData";
import { WeekdayHeader } from "../models/WeekdayHeader";
import { Entry } from "../models/Entry";
import { registerAppDevToolsAfterLogin } from "../lib/Devtools";
import { TActivityToReservationMapObj } from "../types/api/shared";
import TempLogListenerSelector from "./TempLogListenerSelector";

import * as Sentry from "@sentry/browser";

import {
    isSameTrack,
    isSameActivities,
    calculateWeeks,
    selectType,
} from "./preferences/PrefsCoreAPI.utils";
import { EAppId } from "@timeedit/types/lib/enums/appIds.enum";
import { EOrganizationVisibility } from "@timeedit/types/lib/enums/organizationVisibility.enum";
import { EAppEnv } from "@timeedit/types/lib/enums/appEnv.enum";
import { ERegion } from "@timeedit/types/lib/enums/region.enum";
import { ETokenType } from "@timeedit/types/lib/enums/token.enum";
import { TColorDefClass, TObjectIdClass, TTypeNameClass } from "../types/api/APIClassTypes";
import {
    IGetServerEnvResponse,
    ITargetExternalAppsEnvironmentResponse,
} from "../types/api/APIResponseTypes";
import coreAPI from "./exam/ExamCoreAPI";
import { ITEToken, TOrganizationPublic } from "@timeedit/types";
import CoreTopBar from "./coretopbar/CoreTopBar";
import { TReservationLayer } from "../types/models/TReservationLayer";
import { AppContextProvider } from "../contexts/AppContext";
import { withAuthContext, TAuthContextProp } from "../contexts/AuthContextUtils";
import { ObjectSearch } from "../models/ObjectSearch";
import TeuiMassChangeBar from "./TeuiMassChangeBar";
import NamedWeek from "../lib/NamedWeek";
import { PrefsData } from "../models/PrefsData";
import Icon from "@timeedit/ui-components/components/Icon/Icon";

const { createReservationGroupNameFromTrack, getTrackData } = ActivityUtils;

const UpdateTimeConstants = {
    INDICATE_UPDATE: 6 * 60 * 60 * 1000, // 6 hours

    UPDATE_IF_INACTIVE: 36 * 60 * 60 * 1000, // 36 hours

    FORCE_UPDATE: 48 * 60 * 60 * 1000, // 48 hours
};

const AM_ACTION = {
    SCHEDULE: "schedule",
    CANCEL: "cancel",
} as const;

const INACTIVITY_THRESHOLD_TIME = 10 * 60 * 1000; // 10 minutes
const FIVEHUNDRED_MILLIS = 500;

const LAYER_KIND_EXPERIMENT = 3;

const MOUSETRAP_ESCAPE = "esc";

const pushToFront = (array, value) => {
    const result = array.filter((val) => val !== value);
    result.splice(0, 0, value);
    return result;
};

const hasExamId = (reservation, examFieldId?: number) => {
    let hasId = false;
    if (!reservation.fields) {
        return hasId;
    }
    reservation.fields.forEach((field) => {
        if (field.id === examFieldId) {
            if (field.values && field.values.length > 0 && field.values[0] !== "") {
                hasId = true;
            }
        }
    });
    return hasId;
};

const getLanguageString = function () {
    return navigator.languages ? navigator.languages[0] : navigator.language;
};

const anyCalendarHasWeekdayHeader = (allCalendars: TComponentType[]) => {
    if (allCalendars.length === 0) {
        return false;
    }
    return allCalendars.findIndex(
        (calendar) => calendar instanceof Calendar && calendar.hasHeaderType(WeekdayHeader)
    );
};

const doPrompt = (
    message?: string | undefined,
    _default?: string | undefined
): string | undefined => prompt(message, _default) ?? undefined;

const trackFits = (track?: unknown[], weeks?: unknown[]) => {
    return track && weeks && track.length >= weeks.length;
};

let primaryFieldManager;
let _prevEscBinding = [];

export type TAppState = {
    width: number;
    height: number;
    showSelection: boolean;
    showCalendarIds: boolean;
    selectionHeight: number;
    activeCalendar: number;
    aboutVisible: boolean;
    commandBarVisible: boolean;
    shortcutOverlayVisible: boolean;
    macros: Macros;
    objectSearch: null | ObjectSearch;
    currentFluffyItem: null;
    menuPosition: "left" | "right";
    reservationInfoIds: number[];
    orderInfoIds: number[];
    isEntryInfo: boolean;
    copiedEntry: Entry | null;
    copiedGroupEntries: Entry[] | null;
    updateAvailable: number | null;
    massChangeEnabled: boolean;
    activeLayer: number;
    massChangeProgress: number;
    filters: any[];
    colorDefs: TColorDefClass[];
    objectIds: number[];
    dynamicReservationIds: number[];
    prefsComponentActive: boolean;
    examComponentActive: boolean;
    memberCreatorActive: boolean;
    targetExternalAppsEnvironment: Partial<ITargetExternalAppsEnvironmentResponse["parameters"][0]>;
    hasReservationExceptions: boolean;
    multiPanelOrder: Array<"object" | "reservation" | "order">;
    skipBackgroundText: boolean;
    showEnableCustomWeekNames: boolean;
    customWeekNames: NamedWeek[];
    objectRequestCallback: ((ids: number[] | null) => void) | null;
    layers: TReservationLayer[];
    activityManagerReservationCallback: ((reservationIds: number[]) => void) | null;
    examAPI: ReturnType<typeof coreAPI> | null;
    examRequest: unknown[] | null;
    examIdField: number | undefined;
    examTemplate: string | undefined;
    loadFinished: boolean;
    schedulingTracks: {};
    currentTrack: TrackVariation | null;
    trackSchedulingCallback: ((obj: { action: string; payload?: any }) => void) | null;
    finishedTracks: string[];
    mappedResult: TActivityToReservationMapObj[];
    scratchpad: Scratchpad;
    lockedReservationIds: number[];
    createdGroups: { [groupName: string]: number };
    hasOldExamComponent: boolean;
    timezones: {
        shortName: string;
        name: string;
        offset: string;
    }[];
    modalDialogVisible: boolean;
    floatingWindows: TFloatingWindowEntry[];
    trackReservationMode?: string;
    isInfoEntryEdited?: boolean;
    modalDialog?: React.JSX.Element | null;
    secondModalDialog?: React.JSX.Element | null;
    secondModalDialogVisible?: boolean;
    copySelectedObject?: boolean;
    unlockedReservations?: number[] | null;
    skipConfirmation?: boolean;
    temporaryUnlock?: boolean;
    supportInfoLines?: string[];
    showSupportText?: boolean;
    dialogPosition?: React.CSSProperties["position"];
    dialogLeft?: string;
    dialogTop?: string;
    isWorking?: boolean;
    dialogProps?: {
        content: any;
        displayKey: string | null;
        title: string | null;
        buttons: any;
        onRemember: () => void;
        useContentSize: boolean;
        hasCopyButton: boolean;
        isModernStyle: boolean;
        okButtonTitle: string | null;
    };
    types?: number[];
    organization?: TOrganizationPublic;
};

type TAppProps = {
    flags: LDFlagSet;
    ldClient?: LDClient;
    user?: User;
    env: IGetServerEnvResponse["parameters"][0];
    view: View;
    token: ITEToken;
    logoutAuth: boolean;
    lastMousePosition: MouseEvent;
    menu: MenuModel;
    selection: Selection;
} & TResizableComponentStateAndProps &
    TAuthContextProp;

export class App extends React.Component<TAppProps, TAppState> {
    moveLayers = {};
    macros = [
        { action: Macros.Action.SET_ORDER },
        { action: Macros.Action.SET_OBJECT },
        { action: Macros.Action.SET_RESERVATION },
    ];
    MIN_BELOWBAR_HEIGHT = 180;

    reloadFunctions: Array<() => void> = [];
    private _reloadTimeout?: NodeJS.Timer;
    private _lastComponentActivity?: number;
    private _dragOffsetLeft?: number;
    private _dragOffsetTop?: number;
    private _previousEscBinding: any;
    private _objectManagerCloseCheck?: () => boolean;
    private _closeObjectManagerWindow: (() => void) | null = null;
    private _isReloading?: boolean;
    private _indicationEventRecorded?: boolean;
    private _dynamicReservationInfoTimeout: NodeJS.Timeout | null = null;

    TARGET = "_blank";

    constructor(props) {
        super(props);

        this.MIN_BELOWBAR_HEIGHT = 180;

        this.state = {
            showSelection: true,
            showCalendarIds: false,
            selectionHeight: 230,
            activeCalendar: 0,
            aboutVisible: false,
            commandBarVisible: false,
            shortcutOverlayVisible: false,
            macros: new Macros().freeze(),
            objectSearch: null,
            currentFluffyItem: null,
            menuPosition: MenuModel.POSITION.LEFT,
            reservationInfoIds: [],
            orderInfoIds: [],
            isEntryInfo: false,
            copiedEntry: null,
            copiedGroupEntries: [],
            updateAvailable: null,
            massChangeEnabled: true,
            activeLayer: 0,
            massChangeProgress: 0,
            filters: [],
            colorDefs: [],
            objectIds: [],
            dynamicReservationIds: [],
            prefsComponentActive: false,
            examComponentActive: false,
            memberCreatorActive: false,
            targetExternalAppsEnvironment: {},
            hasReservationExceptions: false,
            multiPanelOrder: ["object", "reservation", "order"],
            skipBackgroundText: false,
            showEnableCustomWeekNames: false,
            customWeekNames: [],
            objectRequestCallback: null,
            layers: [],
            activityManagerReservationCallback: null,
            examAPI: null,
            examRequest: null,
            examIdField: undefined,
            examTemplate: undefined,
            loadFinished: false,
            schedulingTracks: {},
            currentTrack: null,
            trackSchedulingCallback: null,
            finishedTracks: [],
            mappedResult: [],
            scratchpad: new Scratchpad(this.onPinboardChanged, (eventKey, data) =>
                this.props.ldClient?.track(eventKey, data)
            ),
            lockedReservationIds: [],
            createdGroups: {},
            width: 0,
            height: 0,
            hasOldExamComponent: false,
            timezones: [],
            modalDialogVisible: false,
            floatingWindows: [],
        };
    }

    doLogout = () => {
        if (this.props.authContext?.logout) {
            this.props.authContext.logout();
        }
    };

    getAuthState = () => {
        return this.props.authContext?.authState || "";
    };

    private cachedContext?: ReturnType<App["buildContext"]>;

    /**
     * Returns the previous context object when no field has changed, so
     * AppContext consumers don't re-render on every App render.
     *
     * We need this optimization until we introduce proper state management.
     */
    getContext = () => {
        const next = this.buildContext();
        const prev = this.cachedContext;
        if (prev && Object.keys(next).every((key) => prev[key] === next[key])) {
            return prev;
        }
        this.cachedContext = next;
        return next;
    };

    /**
     * TODO: Move to proper state context managerment (redux or createContext hook)
     */
    private buildContext = () => {
        return {
            update: TimeEdit.State.replace,
            user: this.props.user,
            registerMacro: this.registerMacro,
            fireEvent: this.fireEvent,
            deregisterMacro: this.deregisterMacro,
            presentModal: this.presentModal,
            presentModernModal: this.presentModernModal,
            presentFloatingWindow: this.presentFloatingWindow,
            dismissFloatingWindow: this.dismissFloatingWindow,
            dismissModal: this.handleDismissModal,
            focusFirstCalendar: this.focusFirstCalendar,
            getAppSize: this.getAppSize,
            colorDefs: this.state.colorDefs,
            prefsComponentActive: this.state.prefsComponentActive,
            examComponentActive: this.state.examComponentActive,
            memberCreatorActive: this.state.memberCreatorActive,
            targetExternalAppsEnvironment: this.state.targetExternalAppsEnvironment,
            hasReservationExceptions: this.state.hasReservationExceptions,
            getViewId: this.getViewId,
            showEnableCustomWeekNames: this.state.showEnableCustomWeekNames,
            customWeekNames: this.state.customWeekNames,
            primaryFieldManager,
            registerExamAPI: this.registerExamAPI,
            createExamRequest: this.createExamRequest,
            examRequest: this.state.examRequest,
            toggleSelection: this.handleToggleSelection,
            examIdField: this.state.examIdField,
            examTemplate: this.state.examTemplate,
            clearExamRequest: this.clearExamRequest,
            getAuthState: this.getAuthState,
            ssoLogout: this.doLogout,
            env: this.props.env,
            organizationId: this.props.token?.organizationId ?? undefined,
            isLockingEnabled: this.isLockingEnabled,
            scratchpad: this.state.scratchpad,
            envIsNotProduction: this.envIsNotProduction,
        };
    };

    isLockingEnabled = () => {
        return true;
    };

    envIsBeta = () => {
        return process.env.NODE_ENV === "development" || this.props.env.serverEnv === "beta";
    };

    envIsNotProduction = () => {
        return process.env.NODE_ENV === "development" || this.props.env.serverEnv !== "production";
    };

    createExamRequest = (data, mode?) => {
        // Yes, we should be able to merge this into a single code path and always use an array
        // But we want to keep things separate for the Exam plugin in the first version
        const offset = TimeConstants.UNIX_MILLENNIUM_OFFSET;
        if (Array.isArray(data)) {
            const fieldIds: number[] = [];
            const mappedData = data.map((d) => {
                const mapped = { ...d };
                mapped.begin = d.begin + offset;
                mapped.end = d.end + offset;
                mapped.modified = d.modified + offset;
                mapped.created = d.created + offset;
                mapped.mode = mode;
                fieldIds.push(...mapped.fields.map((field) => field.id));
                return mapped;
            });
            API.getFields(fieldIds, (fields) => {
                // Map extids to all fields
                fields.forEach((field) => {
                    mappedData.forEach((data) => {
                        data.fields.forEach((f) => {
                            if (f.id === field.id) {
                                f.extid = field.extid;
                            }
                        });
                    });
                });

                if (!this.state.examAPI) {
                    this.setState({ examRequest: mappedData }, () =>
                        this.toggleExamComponent(false)
                    );
                } else {
                    this.fireEvent(`app`, Macros.Event.EXAM_REQUEST_UPDATE, mappedData);
                }
            });
        } else {
            // Fire event to create Exam component
            // Use same logic as for the reservation list keyboard shortcut
            const mappedData = { ...data };
            mappedData.begin = data.begin + offset;
            mappedData.end = data.end + offset;
            mappedData.modified = data.modified + offset;
            mappedData.created = data.created + offset;
            mappedData.mode = mode;
            API.getFields(
                data.fields.map((field) => field.id),
                (fields) => {
                    fields.forEach((field, index) => {
                        mappedData.fields[index].extid = field.extid;
                    });
                    if (!this.state.examAPI) {
                        this.setState({ examRequest: mappedData }, () =>
                            this.toggleExamComponent(false)
                        );
                    } else {
                        this.fireEvent(`app`, Macros.Event.EXAM_REQUEST_UPDATE, mappedData);
                    }
                }
            );
        }
    };

    clearExamRequest = (callback?) => {
        this.setState({ examRequest: null }, callback);
    };

    registerExamAPI = (examAPI) => {
        if (this.state.examRequest) {
            let requests;
            if (Array.isArray(this.state.examRequest)) {
                requests = this.state.examRequest.map((request) => Object.assign({}, request));
            } else {
                requests = Object.assign({}, this.state.examRequest);
            }
            setTimeout(() => {
                this.fireEvent(`app`, Macros.Event.EXAM_REQUEST_UPDATE, requests);
            }, FIVEHUNDRED_MILLIS);
        }
        this.setState({ examAPI, examRequest: null });
    };

    clearSchedulingTracks = () => {
        // Can be invoked while no track scheduling is in progress
        const tCb = this.state.trackSchedulingCallback;
        this.setState(
            {
                schedulingTracks: {},
                currentTrack: null,
                trackSchedulingCallback: null,
                finishedTracks: [],
                mappedResult: [],
                createdGroups: {},
            },
            () => {
                if (tCb) {
                    tCb({ action: AM_ACTION.CANCEL });
                }
            }
        );
    };

    setSchedulingTracks = (tracks, onScheduleTracks, reservationMode?: string) => {
        const trackKeys = Object.keys(tracks);

        const allActivitiesScheduled = (track): boolean =>
            track.activities.every((activity) => activity.reservationId);

        const key =
            trackKeys.find((trackKey) => !allActivitiesScheduled(tracks[trackKey])) || trackKeys[0];

        const finishedTracks = trackKeys.filter((trackKey) =>
            allActivitiesScheduled(tracks[trackKey])
        );

        const mappedResult: TActivityToReservationMapObj[] = trackKeys
            .flatMap((trackKey) => tracks[trackKey].activities)
            .filter(({ reservationId }) => Boolean(reservationId))
            .map(({ activityId, reservationId }) => ({
                activityId,
                reservationId,
            }));

        this.setState(
            {
                schedulingTracks: tracks,
                currentTrack: tracks[key],
                trackSchedulingCallback: onScheduleTracks,
                finishedTracks,
                trackReservationMode: reservationMode,
                mappedResult,
            },
            () => {
                this.onCurrentTrackChanged([key]);
            }
        );
    };

    goToNextTrack = (currentTrack, mappedResult: TActivityToReservationMapObj[], createdGroups) => {
        const currentKey = Object.keys(this.state.schedulingTracks).find((key) =>
            isSameTrack(this.state.schedulingTracks[key], currentTrack)
        );
        // TODO: Error handling if currentKey not found?
        if (!currentKey) {
            Log.error("Current track not found in scheduling tracks");
            return;
        }

        const mappedActivities = mappedResult.map(({ activityId }) => activityId);

        const currentTrackFinished = currentTrack.activities.every(
            (activity) => mappedActivities.includes(activity.activityId) || activity.reservationId
        );

        if (!currentTrackFinished) {
            this.setState({ mappedResult, createdGroups }, () => {
                // Preserve/re-set duration here, if any
                this.onCurrentTrackChanged([currentKey]);
            });
            return;
        }

        const finishedTracks: string[] = this.state.finishedTracks.includes(currentKey)
            ? this.state.finishedTracks
            : [...this.state.finishedTracks, currentKey];

        const nextTrackToSchedule = Object.keys(this.state.schedulingTracks).find(
            (key) => !finishedTracks.includes(key)
        );

        if (!nextTrackToSchedule) {
            // All tracks scheduled. Burst of confetti?
            this.clearSchedulingTracks();
            return;
        }

        this.setState({ finishedTracks, mappedResult, createdGroups }, () => {
            this.onCurrentTrackChanged([nextTrackToSchedule]);
        });
    };

    onCurrentTrackChanged = (trackKey) => {
        // Array of strings

        const updateObjects = () => {
            if (!this.state.currentTrack || this.state.currentTrack.activities.length === 0) {
                return;
            }
            const { objects, filters, fields } = getTrackData(this.state.currentTrack.activities);

            API.getObjectNamesByExtid(objects.map((obj) => obj.value).flat(), false, (objs) => {
                const typeArray = [
                    ...objects.map((obj) => obj.extId),
                    ...filters.map((flt) => flt.extId),
                ];
                API.getTypesByExtid(typeArray, true, (types) => {
                    API.getFieldsByExtid(
                        fields.map((f) => f.extId),
                        (foundFields) => {
                            const resultingFields = fields.map((field) => {
                                const result = { ...field };
                                result.extid = field.extId;
                                result.fieldExtId = field.extId;
                                const foundField = _.find(
                                    foundFields.filter((f) => Boolean(f)),
                                    (ff) => ff.extid === result.extid
                                );
                                if (foundField) {
                                    result.id = foundField.id;
                                } else {
                                    Log.error(`No object with external ID ${field.extId} found.`);
                                }
                                result.values = (
                                    Array.isArray(field.value) ? field.value : [field.value]
                                )
                                    .map((value) => {
                                        // Server wants boolean field values as strings
                                        if (value === true || value === "true") {
                                            return "1";
                                        }
                                        if (value === false || value === "false") {
                                            return "0";
                                        }
                                        if (value === "null") {
                                            return null;
                                        }
                                        return value;
                                    })
                                    .filter((value) => value !== null);

                                return result;
                            });

                            this.fireEvent(
                                `app`,
                                Macros.Event.SET_EXTERNAL_SELECTION,
                                {
                                    fields: resultingFields,
                                    objects: objs.map((obj, index) => {
                                        const type = types[index];
                                        if (type) {
                                            return Object.assign({}, obj, {
                                                type,
                                                typeId: type.id,
                                            });
                                        }
                                        Log.error(
                                            `No type with external ID ${typeArray[index]} found.`
                                        );
                                        return obj;
                                    }),
                                    duration:
                                        this.state.schedulingTracks[trackKey[0]].activities[0]
                                            .duration,
                                    reservationMode: this.state.trackReservationMode, // SCHED-1193: Add in reservation mode from wherever AM passes it in
                                },
                                () => {
                                    if (filters && filters.length > 0) {
                                        const searchCriteria = toSearchCriteriaFromObjects(
                                            filters
                                        ).map((filter) => {
                                            const type = _.find(
                                                types,
                                                (tp) => tp && tp.extid === filter.type.extid
                                            );
                                            if (type) {
                                                return Object.assign({}, filter, {
                                                    type,
                                                });
                                            }
                                            Log.error(
                                                `No type with external ID ${filter.type.extid} found.`
                                            );
                                            return filter;
                                        });
                                        this.fireEvent(
                                            `app`,
                                            Macros.Event.SET_EXTERNAL_OBJECT_SEARCH_CRITERIA,
                                            {
                                                searchCriteria,
                                            },
                                            () => {
                                                const type = searchCriteria[0].type;
                                                if (type) {
                                                    selectType(
                                                        type.extid,
                                                        (result: TTypeNameClass) => {
                                                            this.fireEvent(
                                                                `app`,
                                                                Macros.Event.SELECT_EXTERNAL_TYPE,
                                                                result,
                                                                () => {
                                                                    console.log("Done?");
                                                                }
                                                            );
                                                        }
                                                    );
                                                }
                                            }
                                        );
                                    }
                                }
                            );
                        }
                    );
                });
            });
        };
        let nextActivities = this.state.schedulingTracks[trackKey[0]].activities.filter(
            (activity) => !ActivityUtils.isReservationId(activity.reservationId)
        );
        const currentTrackUnscheduledActivities = this.state?.currentTrack?.activities.filter(
            (activity) => !ActivityUtils.isReservationId(activity.reservationId)
        );
        const currentWeeks = this.state?.currentTrack?.activities.map((activity) => activity.week);
        const nextWeeks = nextActivities.map((activity) => activity.week);

        const doSetTrack = (focusReservationId) => {
            if (focusReservationId) {
                this.setState(
                    {
                        currentTrack: {
                            ...this.state.schedulingTracks[trackKey[0]],
                            activities: nextActivities,
                        },
                    },
                    () => {
                        API.exportReservations([focusReservationId], false, (reservations) => {
                            this.fireEvent(
                                `prefsInCore`,
                                Macros.Event.SELECT_RESERVATION,
                                reservations[0]
                            );
                            this.handleEntryInfoOpen([focusReservationId], false);
                            /*const activeCalendar = this.getActiveCalendar();
                        TimeEdit.State.replace(
                            activeCalendar,
                            activeCalendar.gotoReservation(focusReservationId)
                        );*/
                        });
                    }
                );
            } else if (isSameActivities(currentTrackUnscheduledActivities, nextActivities)) {
                updateObjects();
            } else {
                // Preserve track information, but only work with unscheduled activities
                this.setState(
                    {
                        currentTrack: {
                            ...this.state.schedulingTracks[trackKey[0]],
                            activities: nextActivities,
                        },
                    },
                    updateObjects
                );
            }
        };

        let focusReservationId: number | null = null;
        if (nextWeeks.length === 0) {
            // If everything is already scheduled, set all weeks instead of no weeks
            nextActivities = this.state.schedulingTracks[trackKey[0]].activities;
            focusReservationId = parseInt(nextActivities[0].reservationId, 10);
        }
        this.setWeeks(calculateWeeks(nextActivities), () => {
            doSetTrack(focusReservationId);
        });
    };

    /**
     * Adds the reservations to an existing reservation group for the track, or creates a new one if it doesn't exist yet, then goes to the next track in the scheduling flow.
     */
    private addToReservationGroup = ({
        track,
        reservationIds,
        mappedResult,
        callback,
    }: {
        track: TrackVariation;
        reservationIds: number[];
        mappedResult: TActivityToReservationMapObj[];
        callback: (mappedResult: TActivityToReservationMapObj[]) => void;
    }) => {
        const groupName = createReservationGroupNameFromTrack(track);
        const existingGroupId = this.state.createdGroups[groupName];

        if (existingGroupId) {
            // Add to this group instead of creating a new one
            API.addReservationsToReservationGroup(existingGroupId, reservationIds, (addResult) => {
                if (addResult.details) {
                    Log.info("Error adding to reservation group", { code: addResult.result });
                    // TODO: Missing next steps? Callback with results so far? Some explicit abort?
                } else {
                    callback(mappedResult);
                    this.goToNextTrack(track, mappedResult, this.state.createdGroups);
                }
            });
            return;
        }

        // Create group and add reservations to it
        API.createReservationGroupWithReservations(
            groupName,
            reservationIds,
            (groupId) => {
                Log.info("Reservation group created");
                const updatedGroups = { ...this.state.createdGroups };
                updatedGroups[groupName] = groupId;
                callback(mappedResult);
                this.goToNextTrack(track, mappedResult, updatedGroups);
                // Do we want to do something similar here to what we do in Calendar when a group is created?
            },
            (error) => {
                // The details belong in the log context, which is what MessageBar renders.
                Log.info("Error creating reservation group", { details: error ? [error] : [] });
            }
        );
    };

    mapReservationsToActivities = (reservationIds, callback) => {
        API.exportReservations(reservationIds, true, (fullReservations) => {
            // Get the week for each reservation
            // Match the week pattern to set tracks/activities
            // A reservation with no time cannot sit in a track; week 0 matches none,
            // so trackFits rejects the batch below.
            const weeks = fullReservations.map((reservation) =>
                new MillenniumDateTime(reservation.begin ?? 0)
                    .getMillenniumDate()
                    .getMillenniumWeek(Language.firstDayOfWeek, Language.daysInFirstWeek)
            );

            const track = this.state.currentTrack;
            if (!track || !trackFits(track.activities, weeks)) {
                callback([]);
                return;
            }

            const alreadyMappedReservationIds = this.state.mappedResult.map(
                ({ reservationId }) => reservationId
            );

            const mappedResult: TActivityToReservationMapObj[] = [
                ...this.state.mappedResult,
                ...fullReservations
                    .filter(({ activityId }) => Boolean(activityId))
                    .filter(({ id }) => !alreadyMappedReservationIds.includes(id))
                    .map((fR) => ({ activityId: fR.activityId!, reservationId: fR.id! })),
            ];

            if (this.props.user?.createGroupsForReservationTracks) {
                this.addToReservationGroup({ track, reservationIds, mappedResult, callback });
                return;
            }

            callback(mappedResult);
            this.goToNextTrack(track, mappedResult, this.state.createdGroups);
        });
    };

    onPinboardChanged = (objects) => {
        const obs = Array.isArray(objects) ? [JSON.stringify(objects)] : [];
        API.setPreferences("pinboardObjects", obs, _.noop);
        this.setState({}); // Intentional empty set to explicitly get a rerender, updating pinboard components wherever they appear
    };

    getViewId = () => {
        return this.props.view ? this.props.view.id : 0;
    };

    getAppSize = () => {
        return { width: this.state.width, height: this.state.height };
    };

    setupMousetrap = () => {
        const forceUpdate = () => {
            if (this._isReloading) {
                return;
            }
            if (window.confirm(Language.get("nc_client_update_needed"))) {
                this._isReloading = true;
                document.location.reload();
            }
        };
        API.getMessageDispatcher().on("updateAvailable", () => {
            // Register the first time the client becomes aware of an available update
            if (!this.state.updateAvailable) {
                this.setState({ updateAvailable: Date.now() });
                return;
            }

            // After being aware for UPDATE_IF_INACTIVE, set a timeout that waits for
            // the client to be inactive for INACTIVITY_THRESHOLD_TIME and reload the page at that point
            if (
                !this._reloadTimeout &&
                Date.now() >= this.state.updateAvailable + UpdateTimeConstants.UPDATE_IF_INACTIVE
            ) {
                const INACTIVE_CHECK_INTERVAL = 5000;

                this._reloadTimeout = setInterval(() => {
                    if (
                        this._lastComponentActivity &&
                        Date.now() >= this._lastComponentActivity + INACTIVITY_THRESHOLD_TIME
                    ) {
                        forceUpdate();
                    }
                }, INACTIVE_CHECK_INTERVAL);
                return;
            }

            // After being aware for FORCE_UPDATE, force an update no matter what
            if (Date.now() >= this.state.updateAvailable + UpdateTimeConstants.FORCE_UPDATE) {
                forceUpdate();
            }
        });

        const registeredShortcuts = {};

        Mousetrap.bindWithHelp = function (
            keys,
            callback,
            action = undefined,
            description = "",
            hidden = false
        ) {
            if (!callback || callback === Mousetrap.noop) {
                return;
            }
            if (keys === MOUSETRAP_ESCAPE && description === "") {
                // eslint-disable-next-line no-param-reassign
                description = Language.get("nc_shortcut_text_cancel.");
            }
            if (!hidden || process.env.NODE_ENV === "development") {
                registeredShortcuts[keys] = { keys, description, hidden };
            }
            if (!callback.isWrapped) {
                const wrapped = Mousetrap.bind(keys, (event) => callback(event) || false, action);
                wrapped.isWrapped = true;
            } else {
                Mousetrap.bind(keys, callback, action);
            }
        };

        Mousetrap.unbindWithHelp = function (keys, action, returnPrevious) {
            delete registeredShortcuts[keys];
            return Mousetrap.unbind(keys, action, returnPrevious);
        };

        Mousetrap.getShortcuts = function () {
            return registeredShortcuts;
        };

        Mousetrap.presentShortcut = function (keyCombinations) {
            if (keyCombinations instanceof Array) {
                // eslint-disable-next-line no-param-reassign
                keyCombinations = keyCombinations.join(", ");
            }

            const KEYS = [
                "shift",
                "up",
                "down",
                "left",
                "right",
                "backspace",
                "space",
                "mod",
                "alt",
                "esc",
                "enter",
                "del",
            ];
            return KEYS.reduce((output, key) => {
                const regex = new RegExp(`(\\+|\\s|^)${key}(\\+|\\s|$)`);
                return output.replace(regex, `$1${Language.get(`nc_key_${key}` as any)}$2`);
            }, keyCombinations);
        };

        Mousetrap.bindWithHelp(
            "shift+mod+alt+c",
            () => {
                window.localStorage.clear();
            },
            undefined,
            Language.get("nc_help_delete_locally_stored_information.")
        );

        // Override of default Mousetrap event stopping to allow handling of enter and escape when in input and select form elements.
        Mousetrap.stopCallback = function (e, element, combo) {
            // If the element has the class "mousetrap" then no need to stop
            if (` ${element.className} `.indexOf(" mousetrap ") > -1) {
                return false;
            }

            // Stop for input, select, and textarea. Unless it's enter or escape and not in a text area
            const isFormElement =
                element.tagName === "INPUT" ||
                element.tagName === "SELECT" ||
                element.tagName === "TEXTAREA" ||
                (element.contentEditable && element.contentEditable === "true");
            if (
                isFormElement &&
                (combo === "mod+i" ||
                    combo === "mod+s" ||
                    combo === "mod+e" ||
                    combo === "mod+k" ||
                    combo === MOUSETRAP_ESCAPE)
            ) {
                return false;
            }
            if (element.tagName === "SELECT" && (combo === "mod+c" || combo === "mod+v")) {
                return false;
            }
            if (isFormElement && element.tagName !== "TEXTAREA" && combo === "enter") {
                return false;
            }
            return isFormElement;
        };

        Mousetrap.bindWithHelp(
            "h",
            this.toggleShortcutOverlay,
            undefined,
            Language.get("nc_help_display_shortcuts.")
        );

        Mousetrap.bindWithHelp(
            "mod+i",
            () => {
                API.getSupportInfo((supportURL, supportInfoLines) => {
                    this.toggleAboutDialog(supportInfoLines, true);
                });
            },
            undefined,
            Language.get("nc_about_version_log")
        );

        Mousetrap.bindWithHelp(
            ["<", "mod+k"],
            (event) => {
                event.preventDefault();
                this.toggleCommandBar();
            },
            undefined,
            Language.get("nc_help_show_command_bar.")
        );

        Mousetrap.bindWithHelp(
            "shift+mod+b",
            (event) => {
                event.preventDefault();
                this.toggleReservationList(false);
            },
            undefined,
            Language.get("nc_help_toggle_reservation_list_vertical.")
        );

        Mousetrap.bindWithHelp(
            "shift+alt+mod+b",
            (event) => {
                event.preventDefault();
                this.toggleReservationList(true);
            },
            undefined,
            Language.get("nc_help_toggle_reservation_list_horizontal.")
        );

        Mousetrap.bindWithHelp(
            "shift+mod+a",
            (event) => {
                event.preventDefault();
                this.toggleActivityManager(false);
            },
            undefined,
            Language.get("nc_help_toggle_am_horizontal")
        );

        Mousetrap.bindWithHelp(
            "shift+alt+mod+a",
            (event) => {
                event.preventDefault();
                this.toggleActivityManager(true);
            },
            undefined,
            Language.get("nc_help_toggle_am_vertical")
        );

        Mousetrap.bindWithHelp(
            "alt+right",
            this.changeCalendar.bind(this, 1),
            undefined,
            Language.get("nc_help_select_next_calendar.")
        );
        Mousetrap.bindWithHelp(
            "alt+left",
            this.changeCalendar.bind(this, -1),
            undefined,
            Language.get("nc_help_select_previous_calendar.")
        );
        if (process.env.NODE_ENV === "development") {
            Mousetrap.bindWithHelp(
                "shift+alt+mod+t",
                this.toggleBackgroundText,
                undefined,
                "Toggle calendar background text"
            );
            Mousetrap.bindWithHelp(
                "shift+alt+mod+e",
                () => this.cancelManualReservationOperation(_.noop),
                undefined,
                "Cancel manual activity scheduling"
            );
        }
    };

    loginFinished = () => {
        // Ensure we set logout handler on message dispatcher
        API.getMessageDispatcher().onLogout = this.doLogout;

        this.props.setResizeEndCallback(this.onResizeEnd);
        window.addEventListener("resize", this.updateDimensions);
        if (process.env.NODE_ENV === "development") {
            TimeEdit.scratchpad = this.state.scratchpad;
            registerAppDevToolsAfterLogin(this);
        }

        primaryFieldManager = new PrimaryFieldManager(this.registerMacro, this.fireEvent);
        this.registerMacro(`objectNameCache`, {
            events: [Macros.Event.PRIMARY_FIELD_CHANGED],
            actions: [
                {
                    key: Macros.Action.REFRESH,
                    action: (typeId) => {
                        ObjectNameCache.invalidate(typeId);
                    },
                },
            ],
        });

        const results: any = {};
        const calls = [
            (done) => {
                API.getStartupData((data) => {
                    results.viewId = data.view ? data.view.id : View.DEFAULT_VIEW;
                    results.layerId = data.activeLayer;
                    if (process.env.NODE_ENV === "development") {
                        TimeEdit.colorDefs = data.colorDefs;
                    }
                    results.colorDefs = data.colorDefs;
                    results.timezones = data.timezones || [];
                    TimeEdit.rootType = data.specialTypes[0].id;
                    TimeEdit.locationTypes = data.specialTypes[3].map((type) => type.id);
                    TimeEdit.reservationTextField = data.specialFields[0];
                    TimeEdit.isEmailActive = data.supportsEmail;
                    if (process.env.NODE_ENV === "development") {
                        TimeEdit.isEmailActive = true;
                    }
                    TimeEdit.entryPropertyDefinitions = data.propertyDefs;
                    if (data.supportsExceptions || process.env.NODE_ENV === "development") {
                        results.hasReservationExceptions = true;
                    } else {
                        results.hasReservationExceptions = false;
                    }
                    const texts = [...data.backgroundText];
                    if (process.env.BRANCH) {
                        texts.push(process.env.BRANCH);
                    }
                    TimeEdit.calendarBackgroundTexts = texts;
                    const version = { ...data.versionInfo };
                    const versionString = version.server.version;
                    const versionParts = versionString.split(".");
                    version.server.major = parseInt(versionParts[0], 10);
                    version.server.minor =
                        versionParts.length > 1 ? parseInt(versionParts[1], 10) : 0;
                    TimeEdit.serverInfo = version;
                    if (data.pinboardObjects) {
                        let pinboard = new Scratchpad(this.onPinboardChanged, (eventKey, data) =>
                            this.props.ldClient?.track(eventKey, data)
                        );
                        pinboard.setObjects(JSON.parse(data.pinboardObjects));
                        this.setState({ scratchpad: pinboard });
                        if (process.env.NODE_ENV === "development") {
                            TimeEdit.scratchpad = pinboard;
                        }
                    }

                    this.updateWindowTitle(this.props);
                    if (data.lastFluffy) {
                        let lastFluffy;
                        if (data.lastFluffy) {
                            try {
                                lastFluffy = JSON.parse(data.lastFluffy);
                            } catch (ignore) {
                                // If we fail to parse the last fluffy, we can just move on with our lives.
                                // The only downside is that the user won't see what they had selected at the end of their last session.
                            }
                        }
                        const selection = TimeEdit.State.get()?.selection;
                        if (
                            lastFluffy &&
                            (lastFluffy.template_kind === undefined ||
                                lastFluffy.template_kind === TemplateKind.RESERVATION)
                        ) {
                            const nameItems = lastFluffy.objects
                                .map((obj, index) => ({
                                    id: obj.id,
                                    type: lastFluffy.types[index].id,
                                }))
                                .filter((item) => item.id !== 0);
                            ObjectNameCache.fetchAndPopulate(nameItems, () => {
                                TimeEdit.State.replace(
                                    selection,
                                    selection?.setFluffy(McFluffy.create(lastFluffy))
                                );
                                done();
                            });
                        } else {
                            selection?.createFluffy(null, 0, null, (newSelection) => {
                                TimeEdit.State.replace(selection, newSelection);
                                done();
                            });
                        }
                    } else {
                        done();
                    }
                });
            },

            (done) => {
                User.getCurrent((user) => {
                    const app = TimeEdit.State.get();
                    const menu = app?.menu;
                    // eslint-disable-next-line no-param-reassign
                    user.useTouch = false;
                    TimeEdit.State.update(app, { user });
                    if (user.isAdmin) {
                        const newItems = [
                            ...menu?.items,
                            {
                                id: "admin",
                                label: "user_permission_admin",
                            },
                        ];

                        const newMenu = new MenuModel(newItems);
                        newMenu.freeze();
                        newMenu.updateFromPreferences(this.isNewUI(), (newerMenu) => {
                            TimeEdit.State.update(menu, newerMenu);
                        });
                    } else {
                        menu?.updateFromPreferences(this.isNewUI(), (newMenu) => {
                            TimeEdit.State.update(menu, newMenu);
                        });
                    }
                    this.setState({
                        showSelection: user.showLists,
                        menuPosition: user.menuPosition,
                    });
                    done();
                }, results.timezones?.find((timezone) => timezone.default)?.extid);
            },

            (done) => {
                API.getUserLocale(getLanguageString(), undefined, async (result) => {
                    const res = result.parameters;
                    await Language.setData(res[0], res[1]);
                    SimpleDateFormat.setLocale(Language);
                    this.setupMousetrap();
                    this.registerMacros();
                    done();
                });
            },
            (done) => {
                API.getReservationLayers(LAYER_KIND_EXPERIMENT, (result) => {
                    if (result.length > 0) {
                        results.myLayers = result;
                        API.getPreferences("moveLayers", undefined, undefined, (moveLayers) => {
                            results.moveLayers =
                                moveLayers === null ? {} : JSON.parse(moveLayers as any);
                            done();
                        });
                    } else {
                        results.myLayers = [];
                        results.moveLayers = [];
                        const HELP_TIMEOUT = 2000;
                        setTimeout(() => this.setupHelp(), HELP_TIMEOUT);
                        done();
                    }
                });
            },
            (done) => {
                // todo: once exam feel ready, check for exam scope like we
                // do for AM rather than sending bellow request to the server
                API.hasExternalPermissions(["examincore"], (result) => {
                    if (result[0] === true || process.env.NODE_ENV === "development") {
                        results.hasExamComponent = true;
                    }
                    results.hasPrefsComponent = hasPermissions(
                        this.props.token.appPermissions,
                        this.props.token.scopes,
                        {
                            scopes: ["TE_AM::user"],
                        }
                    );
                    done();
                });
            },
            (done) => {
                if (!this.props.token) {
                    done();
                    return;
                }
                if (!this.props.token.organizationId) {
                    done();
                    return;
                }
                API.getOrgInfo(
                    this.props.env.serverEnv,
                    this.props.token.organizationId,
                    this.props.token.region,
                    (result) => {
                        console.log(result);
                        results.organization = result?.results[0];
                        done();
                    }
                );
            },
            (done) => {
                API.targetExternalAppsEnvironment((envSettings) => {
                    results.targetExternalAppsEnvironment = envSettings;
                    if (process.env.NODE_ENV === "development") {
                        results.examIdField = 200;
                    }
                    const environment = this.props.env.serverEnv;

                    API.getExamSettings(
                        environment,
                        (es) => {
                            console.log("Got Exam settings for environment", environment, es);
                            if (!es) {
                                console.log("Got no Exam settings", es);
                                done();
                            } else if (es.paramMappings) {
                                // Dig out the Exam settings
                                if (es.paramMappings) {
                                    // Platform v3 and later
                                    const examSettings = es.paramMappings.find(
                                        (setting) => setting.appFieldId === "exam_id"
                                    );
                                    if (
                                        examSettings &&
                                        examSettings.fieldExt &&
                                        examSettings.fieldExt !== null &&
                                        examSettings.fieldExt !== ""
                                    ) {
                                        // Dig out the relevant settings from there
                                        API.getFieldsByExtid([examSettings.fieldExt], (fields) => {
                                            if (fields && fields[0]) {
                                                results.examIdField = fields[0].id;
                                            }
                                            done();
                                        });
                                    } else {
                                        done();
                                    }
                                } else {
                                    done();
                                }
                            } else {
                                const examError = es as { code?: number; status?: number };
                                switch (examError.code || examError.status) {
                                    case EXAM_NOT_AUTHORIZED:
                                        console.log(
                                            `Not authorized to access Exam environment '${
                                                envSettings.exam || "beta"
                                            }'. Ask engineering to check tokens.`
                                        );
                                        break;
                                    case EXAM_NO_ORG_ACCESS:
                                        console.log(
                                            `The customer signature '${
                                                TimeEdit.serverInfo
                                                    ? TimeEdit.serverInfo.server.signature
                                                    : "unknown"
                                            }' does not have organization access to Exam.`
                                        );
                                        break;
                                    case EXAM_NOT_FOUND:
                                        console.log(
                                            `The customer signature '${
                                                TimeEdit.serverInfo
                                                    ? TimeEdit.serverInfo.server.signature
                                                    : "unknown"
                                            }' has not been set up in Exam environment '${
                                                envSettings.exam || "beta"
                                            }'. Perhaps it lacks an initial 'cloud_'?`
                                        );
                                        break;
                                    default:
                                }
                                done();
                            }
                        },

                        (error) => {
                            console.log("Error getting Exam settings", error);
                            done();
                        }
                    );
                });
            },
            (done) => {
                API.findSumSettings((result) => {
                    API.getSumSetting(result[0], (filters) => {
                        results.filters = filters;
                        done();
                    });
                });
            },
            (done) => {
                const DAYS_BACK = 365;
                const DAYS_AHEAD = 1095;
                API.showEnableCustomWeekNames((result) => {
                    results.showEnableCustomWeekNames = result;
                    if (result === true) {
                        API.getCustomWeekNames(
                            MillenniumDate.today().getDayNumber() - DAYS_BACK,
                            MillenniumDate.today().getDayNumber() + DAYS_AHEAD,
                            (customWeekNames) => {
                                results.customWeekNames = customWeekNames.filter(
                                    (weekName) => !weekName.isEmpty()
                                );
                                done();
                            }
                        );
                    } else {
                        done();
                    }
                });
            },
        ];

        _.runSync(calls, () => {
            this.setView(results.viewId);
            this.moveLayers = results.moveLayers;
            if (results.layerId) {
                Log.info(Language.get("nc_dialog_working_in_draft_help"));
                this.setActiveLayer(results.layerId);
            } else {
                // Ensure fluffies contain no layer ID
                this.setActiveLayer(0);
            }
            this.setState({
                width: window.innerWidth,
                height: window.innerHeight,
                colorDefs: results.colorDefs,
                prefsComponentActive: results.hasPrefsComponent || false,
                examComponentActive: results.hasExamComponent || false,
                hasOldExamComponent: results.hasExamComponent || false,
                memberCreatorActive:
                    process.env.NODE_ENV === "development" ||
                    this.props.env.serverEnv === "beta" ||
                    this.props.env.serverEnv === "staging" ||
                    this.props.env.serverEnv === "test",
                targetExternalAppsEnvironment: results.targetExternalAppsEnvironment,
                hasReservationExceptions: results.hasReservationExceptions,
                filters: results.filters,
                showEnableCustomWeekNames: results.showEnableCustomWeekNames,
                customWeekNames: this.props.user?.useCustomWeekNames
                    ? results.customWeekNames || []
                    : [],
                layers: results.myLayers,
                examIdField: results.examIdField || undefined,
                examTemplate: results.examTemplate || undefined,
                timezones: results.timezones || [],
                loadFinished: true,
                organization: results.organization,
            });
        });
    };

    componentDidMount() {
        // Login is finished when we have a token
        if (this.props.token === null && this.props.logoutAuth === true) {
            this.doLogout();
            return;
        }

        if (this.props.token) {
            this.loginFinished();
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.props.token === null && this.props.logoutAuth === true) {
            this.doLogout();
            return;
        }
        if (this.props.token && !prevProps.token) {
            this.loginFinished();
        } else if (this.props.token && prevProps.token) {
            // What we want to say here: run the usual componentDidUpdate if already logged in
            this.doUpdateComponent(prevProps);
        }
    }

    doUpdateComponent(prevProps) {
        if (prevProps.view !== this.props.view) {
            this.updateWindowTitle(this.props);
        }

        if (this.props.flags?.examFlowV3 === true) {
            if (this.state.examComponentActive === true) {
                this.setState({ examComponentActive: false });
            }
        } else {
            if (
                this.state.examComponentActive === false &&
                this.state.hasOldExamComponent === true
            ) {
                this.setState({ examComponentActive: true });
            }
        }

        if (this.props.view === prevProps.view || !this.props.view || !prevProps.view) {
            return;
        }

        if (this.props.view.id !== prevProps.view.id) {
            this.setState({ activeCalendar: 0 });
            return;
        }

        const children = this.getAllChildren();
        if (this.state.activeCalendar >= children.length) {
            this.setState({ activeCalendar: 0 });
            return;
        }

        if (this.props.view && (!prevProps.view || this.props.view.id !== prevProps.view.id)) {
            API.getPreferences("activeCalendar", [this.props.view.id], undefined, (result) => {
                if (result && (result as []).length > 0) {
                    this.setActiveCalendar(JSON.parse(result[0]));
                } else {
                    this.setActiveCalendar(
                        this.getAllChildren().findIndex(
                            (child) => child instanceof Calendar && child.selected === true
                        )
                    );
                }
            });
        }

        if (this.state.updateAvailable) {
            this._lastComponentActivity = Date.now();
        }
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.updateDimensions);
        this.deregisterMacro(`primaryFieldManager`);
        this.deregisterMacro(`objectNameCache`);
        this.deregisterMacro("app");
    }

    shouldIndicateUpdate = () => {
        const shouldIndicateUpdate =
            this.state.updateAvailable !== null &&
            Date.now() >= this.state.updateAvailable + UpdateTimeConstants.INDICATE_UPDATE;

        if (shouldIndicateUpdate && !this._indicationEventRecorded) {
            this._indicationEventRecorded = true;
        }

        return shouldIndicateUpdate;
    };

    onResizeEnd = () => {
        const height = Math.max(
            this.state.height - _.getClientPos(this.props.lastMousePosition).y,
            this.MIN_BELOWBAR_HEIGHT
        );
        this.setState({
            selectionHeight: height,
        });
    };

    handleEntryInfoOpen = (
        entry: Entry | Entry[] | number[] | null,
        showPanel = false,
        editMode = false
    ) => {
        let reservationInfoIds: number[] = [];
        if (_.isArray(entry)) {
            reservationInfoIds = _.flatten(
                entry.map((etr: Entry | number) => {
                    if ((etr as Entry).reservationids) {
                        return (etr as Entry).reservationids;
                    }
                    return etr as number;
                })
            );
        } else {
            reservationInfoIds = entry ? entry.reservationids : [];
        }

        // If ids are passed instead of an entry, there might be multiple reservations
        // that are not clustered.
        const isEntryInfo = !_.isArray(entry);

        this.setState({
            isInfoEntryEdited: editMode,
            reservationInfoIds,
            isEntryInfo,
            multiPanelOrder: pushToFront(this.state.multiPanelOrder, "reservation"),
        });
        if (showPanel) {
            TimeEdit.State.replace(this.props.menu, this.props.menu.open("info"));
        }
    };

    handleEditReservationExceptions = (entry: Entry) => {
        let ids = entry.reservationids;
        const selectionGroup = this.getActiveCalendar().selectionGroup;
        if (selectionGroup.length > 1) {
            ids = _.flatten(selectionGroup.map((etr) => etr.reservationids));
        }
        this.presentModernModal(
            <ExceptionEditor
                reservationIds={ids}
                onClose={() => {
                    this.fireEvent(
                        `app`,
                        Macros.Event.RESERVATION_MADE_OR_MODIFIED,
                        [ids],
                        false,
                        false
                    );
                    // Set reservationInfoIds explicitly, causing the info panel to update. Components don't (and shouldn't) react to events sent by themselves
                    this.setState({
                        reservationInfoIds: ids,
                    });
                    this.handleDismissModal();
                }}
                user={TimeEdit.State.get()?.user}
            />,
            null,
            `${Language.get("nc_reservation_exception_title")} (${
                entry.reservationids.length
            } ${Language.get("admin_table_unit_reservation")})`,
            "NO_BUTTONS"
        );
    };

    handleOrderInfoOpen = (orderIds: number[], openIfClosed: boolean = true) => {
        this.setState({
            orderInfoIds: orderIds,
            multiPanelOrder: pushToFront(this.state.multiPanelOrder, "order"),
        });
        if (openIfClosed) {
            TimeEdit.State.replace(this.props.menu, this.props.menu.open("info"));
        }
    };

    onReservationEditChange = (isEdited = true) => {
        this.setState({ isInfoEntryEdited: isEdited });
    };

    onDynamicReservationIdsChanged = (newDynamicIds: number[] = []): void => {
        clearTimeout(this._dynamicReservationInfoTimeout!);
        this._dynamicReservationInfoTimeout = setTimeout(() => {
            this.setState({ dynamicReservationIds: newDynamicIds });
        }, FIVEHUNDRED_MILLIS);
    };

    updateInfoMenuState = () => {
        if (
            (!this.state.reservationInfoIds || this.state.reservationInfoIds.length === 0) &&
            this.state.objectIds.length === 0 &&
            this.state.orderInfoIds.length === 0 &&
            this.state.dynamicReservationIds.length === 0
        ) {
            TimeEdit.State.replace(this.props.menu, this.props.menu.close());
        }
    };

    onReservationInfoClose = () => {
        this.setState({
            reservationInfoIds: [],
        });
    };

    onDynamicReservationInfoClose = () => {
        this.setState({
            dynamicReservationIds: [],
        });
    };

    onObjectInfoClose = () => {
        this.setState({
            types: [],
            objectIds: [],
        });
    };

    onOrderInfoClose = () => {
        this.setState({
            orderInfoIds: [],
        });
    };

    onLayerCreated = (layerId: number, move?: boolean) => {
        if (move !== undefined) {
            this.moveLayers[layerId] = move;
        } else {
            delete this.moveLayers[layerId];
        }
        const allLayerIds = this.state.layers.map((lr) => lr.id);
        Object.keys(this.moveLayers).forEach((key) => {
            const kv = parseInt(key, 10);
            if (allLayerIds.indexOf(kv) === -1) {
                delete this.moveLayers[key];
            }
        });
        API.setPreferences("moveLayers", [JSON.stringify(this.moveLayers)], _.noop);
        this.reloadLayers(() => {
            this.setActiveLayer(layerId || 0);
        });
    };

    setActiveLayer = (layerId = 0) => {
        this.setState(
            {
                activeLayer: layerId || 0,
            },
            () => {
                API.setPreferences("activeLayer", [layerId], _.noop);
                const updatedSelection = this.props.selection.isEditMode()
                    ? this.props.selection.disableEditMode().setLayer(layerId)
                    : this.props.selection.setLayer(layerId);
                const updatedFluffy = updatedSelection.fluffy;
                if (updatedFluffy) {
                    const saveFluffy = updatedFluffy.toJson();
                    API.setPreferences("currentFluffy", [JSON.stringify(saveFluffy)], _.noop);
                }
                TimeEdit.State.replace(this.props.selection, updatedSelection);
            }
        );
    };

    reloadLayers = (callback = _.noop) => {
        API.getReservationLayers(LAYER_KIND_EXPERIMENT, (result) => {
            this.setState({ layers: result }, callback);
        });
    };

    getLayerName = (suggestedName?: string) => {
        let name = doPrompt(Language.get("nc_draft_name_(must_be_unique)"), suggestedName || "");

        const maxLength = 31;
        let length = new TextEncoder().encode(name).length;
        // eslint-disable-next-line no-loop-func
        while (_.any(this.state.layers, (layer) => layer.name === name) || length > maxLength) {
            if (length > maxLength) {
                name = doPrompt(
                    Language.get("err_layer_name_too_long", maxLength, maxLength),
                    name
                );
            } else {
                name = doPrompt(Language.get("nc_draft_name_(must_be_unique)"), name);
            }
            length = new TextEncoder().encode(name).length;
        }
        if (name === "" || _.isNullish(name)) {
            // The user hit cancel in the dialog
            return false;
        }
        return name;
    };

    reportProgress = (newProgress: Partial<TAppState>) => {
        this.setState(newProgress as TAppState);
    };

    performExperiment = (ignoreUnchangedReservations = false, allowDoubleReservations = false) => {
        const persist = () => {
            Experiment.persist(
                this.state.activeLayer,
                ignoreUnchangedReservations,
                allowDoubleReservations,
                this.reportProgress,
                this.presentModal,
                this.setActiveLayer,
                (layerId) => {
                    if (layerId) {
                        this.fireEvent(
                            `app`,
                            Macros.Event.RESERVATION_MADE_OR_MODIFIED,
                            [true],
                            false,
                            false
                        );
                        this.onLayerCreated(layerId);
                    } else {
                        this.reloadLayers();
                    }
                }
            );
        };
        if (!allowDoubleReservations) {
            persist();
        } else {
            this.presentModal(
                <p>{Language.get("nc_mass_change_allow_double_warning")}</p>,
                null,
                Language.get("cal_selected_allow_double_res"),
                [
                    { title: Language.get("dialog_cancel") },
                    { title: Language.get("cal_res_below_ok"), cb: persist },
                ]
            );
        }
    };

    cancelExperiment = (layer?: number) => {
        Experiment.cancel(
            layer || this.state.activeLayer,
            this.reportProgress,
            this.presentModal,
            this.onLayerCreated
        );
    };

    setOrder = (order, descriptionField, commentField) => {
        this.handleOrderInfoOpen([order.id], false);
        const selection = this.props.selection;
        API.getTemplateGroups(selection?.fluffy?.templateKind.number, (templateGroups) => {
            const templateGroupIds = templateGroups.parameters[0].map((group) => group.id);
            let setResult: any = false;
            const fields: { id: number; values: string[] }[] = [];
            if (!_.isNullish(descriptionField)) {
                fields.push({ id: descriptionField, values: [order.description] });
            }
            if (!_.isNullish(commentField)) {
                fields.push({ id: commentField, values: [order.comment] });
            }

            _.runAsync(
                [
                    (done) => {
                        const orderFluffy = selection?.fluffy?.toJson();
                        if (!orderFluffy) {
                            done();
                            return;
                        }
                        API.setOrderRowToMcFluffy(
                            order.id,
                            orderFluffy,
                            templateGroupIds,
                            (result) => {
                                setResult = result;
                                done();
                            }
                        );
                    },
                    (done) => {
                        const ids = order.objects.concat(order.extra_objects || []).map((obj) => ({
                            id: obj.id,
                            type: obj.type.id,
                        }));
                        ObjectNameCache.fetchAndPopulate(ids, () => done());
                    },
                ],
                () => {
                    // What do do if !success (result.parameters[1])
                    if (setResult.parameters[1]) {
                        const newFluffy = McFluffy.create(setResult.parameters[0]);
                        newFluffy.rules = setResult.parameters[2];
                        const user = TimeEdit.State.get()?.user;
                        let selectNextItem = user?.jumpToNextType;
                        if (fields.length > 0) {
                            TimeEdit.State.update(
                                selection,
                                selection
                                    ?.setFluffy(newFluffy.setFields(fields))
                                    ?.setSelectNextItem(selectNextItem)
                            );
                        } else {
                            TimeEdit.State.update(
                                selection,
                                selection?.setFluffy(newFluffy)?.setSelectNextItem(selectNextItem)
                            );
                        }
                        this.focusBestCalendar();
                        // Adjusting visible calendar period is done by the calendar listening to the same event
                    }
                }
            );
        });
    };

    handleObjectsSaved = (objectIds: number[]) => {
        if (this.state.objectRequestCallback) {
            this.state.objectRequestCallback(objectIds);
            this.setState({ objectRequestCallback: null });
        }
        this.reloadFunctions.forEach((reloadFunction) => reloadFunction());
    };

    handleFluffyItemChanged = (newFluffyItem) => {
        this.setState({ currentFluffyItem: newFluffyItem });
    };

    mapIds(idList) {
        let ids: number[] = [];
        if (idList instanceof Array) {
            ids = idList.map((item) => item.id || item);
        } else if (idList instanceof Object) {
            ids = [idList.id];
        } else {
            ids = [idList]; // Naked ID
        }
        return ids;
    }

    handleNewObject = (typeId: number, title: string) => {
        this.showObjectManager(this.mapIds(typeId), [], [], false, false, title, true);
    };

    handleObjectInfo = (
        object: TObjectIdClass,
        createCopy: boolean = false,
        viewOnly: boolean = false,
        title: string
    ) => {
        const objectIds = this.mapIds(object);
        this.showObjectManager([], objectIds, [], createCopy, viewOnly, title, false);
    };

    handleSetVisibleCalendarIds = (value) => {
        this.setState({ showCalendarIds: value });
    };

    handleViewChange = (id: number) => {
        this.setView(id);
    };

    setView(id: number) {
        View.find(id, (newView) => {
            if (newView instanceof Error) {
                console.error(newView);
                // Recoverable - we fall back to the default view below.
                Log.error(newView.message);
            }

            if (!newView || newView instanceof Error) {
                View.find(View.DEFAULT_VIEW, (defaultView) => {
                    if (defaultView instanceof Error || !(defaultView instanceof View)) {
                        console.error(defaultView);
                        // Non-recoverable - there is no view left to fall back to.
                        Log.emergency("Could not load default view, please contact support.");
                        TimeEdit.State.update(TimeEdit.State.get()?.setView?.(null));
                        return;
                    }
                    defaultView.id = id;
                    API.setPreferences("view", [{ class: "viewid", id }], _.noop);
                    TimeEdit.State.update(TimeEdit.State.get()?.setView?.(defaultView));
                });
            } else {
                API.setPreferences(
                    "view",
                    [{ class: "viewid", id: newView instanceof View ? newView.id : null }],
                    _.noop
                );
                TimeEdit.State.update(TimeEdit.State.get()?.setView?.(newView));
            }

            return null;
        });
    }

    hasCalendarWithIndex = (index) => {
        return this.getAllChildren().length > index;
    };

    setActiveCalendar = (id: number) => {
        const activeCalendar = this.hasCalendarWithIndex(id) ? id : 0;
        this.setState({ activeCalendar });
    };

    handleActiveCalendarChange = (id: number) => {
        API.setPreferencesWithRefs(
            "activeCalendar",
            [this.props.view.id],
            [JSON.stringify(id)],
            _.noop
        ); // Intentional stringify, otherwise index 0 won't get saved.
        this.setActiveCalendar(id);
    };

    handleDragEnd = () => {
        const user = TimeEdit.State.get()?.user;
        if (this.state.menuPosition !== user?.menuPosition) {
            this.setState({ menuPosition: user?.menuPosition ?? MenuModel.POSITION.LEFT });
        }
    };

    handleDrop = (event: (React.MouseEvent | React.DragEvent) & { dataTransfer: DataTransfer }) => {
        if (!_.isEventDragDataOfType(event, ["application/x-timeedit-menu"])) {
            return;
        }
        event.preventDefault();
        const user = TimeEdit.State.get()?.user;
        if (this.state.menuPosition !== user?.menuPosition) {
            TimeEdit.State.update(user, user?.setMenuPosition(this.state.menuPosition));
        }
    };

    setObjectManagerCloseCheck = (checkFunction: () => boolean) => {
        this._objectManagerCloseCheck = checkFunction;
    };

    handleToggleSelection = (event?) => {
        API.setPreferences("showLists", [!this.state.showSelection], _.noop);
        this.setState({ showSelection: !this.state.showSelection });
        if (event) {
            event.preventDefault();
        }
    };

    handleDismissModal = () => {
        this.setState({ modalDialogVisible: false, modalDialog: null });
    };

    handleDismissSecondModal = () => {
        this.setState({ secondModalDialogVisible: false, secondModalDialog: null });
    };

    isRequest(reservation) {
        if (!reservation.status) {
            return false;
        }
        return reservation.status.some(
            (status) => status.status === ReservationStatus.C_REQUESTED.id
        );
    }

    registerMacros = () => {
        this.registerMacro("app", {
            events: [Macros.Event.SELECT_ORDER],
            actions: [
                {
                    key: Macros.Action.SET_ORDER,
                    action: (order, descriptionField, commentField) => {
                        if (order && order.id) {
                            this.setOrder(order, descriptionField, commentField);
                        }
                    },
                },
            ],
        });

        this.registerMacro("app", {
            events: [Macros.Event.CHANGE_RESERVATION_TIME],
            actions: [
                {
                    key: Macros.Action.CHANGE_RESERVATION_TIME,
                    action: (
                        reservationIds: number[],
                        skipConfirmation = false,
                        isTemporary = false
                    ) => {
                        const self = this;
                        _prevEscBinding = Mousetrap.unbindWithHelp(MOUSETRAP_ESCAPE, true);
                        Mousetrap.bindWithHelp(MOUSETRAP_ESCAPE, () => {
                            self.endLockMode();
                        });
                        this.setState({
                            unlockedReservations: reservationIds,
                            skipConfirmation,
                            temporaryUnlock: isTemporary,
                        });
                    },
                },
            ],
        });

        this.registerMacro("app", {
            events: [Macros.Event.RESERVATION_MADE_OR_MODIFIED],
            actions: [
                {
                    key: Macros.Action.REFRESH,
                    fireLast: true,
                    action: (
                        reservationIds = [],
                        isNewManualReservation = false,
                        isMadeByExam = false
                    ) => {
                        if (this.state.reservationInfoIds?.length && reservationIds.length === 0) {
                            return;
                        }

                        this.disableTemporarySingleClick();

                        // Inform AM if working with a track
                        if (this.state.currentTrack && isNewManualReservation) {
                            this.mapReservationsToActivities(reservationIds, (mappedResult) => {
                                if (mappedResult && mappedResult.length > 0) {
                                    this.state.trackSchedulingCallback?.({
                                        action: AM_ACTION.SCHEDULE,
                                        payload: mappedResult,
                                    });
                                } else {
                                    Sentry.captureMessage("No mapped result", {
                                        extra: {
                                            reservationIds,
                                            activitiesInTrack:
                                                this.state.currentTrack?.activities.map(
                                                    (activity) => activity.activityId
                                                ),
                                        },
                                        tags: { method: "amTrackScheduling" },
                                    });
                                }
                            });
                        }

                        if (
                            !isMadeByExam &&
                            !isNewManualReservation &&
                            this.state.examIdField &&
                            this.state.examComponentActive
                        ) {
                            API.exportReservations(reservationIds, true, (reservations) => {
                                reservations.forEach((reservation) => {
                                    if (
                                        hasExamId(reservation, this.state.examIdField) &&
                                        // eslint-disable-next-line no-alert
                                        window.confirm(Language.get("nc_update_exam_request"))
                                    ) {
                                        // Open exam plugin
                                        this.createExamRequest(reservation, EXAM_MODE.EDIT);
                                    }
                                });
                            });
                        }
                        if (
                            isNewManualReservation &&
                            this.state.activityManagerReservationCallback !== null
                        ) {
                            const amCallback = this.state.activityManagerReservationCallback;
                            this.setState(
                                {
                                    activityManagerReservationCallback: null,
                                    reservationInfoIds: reservationIds,
                                },
                                () => {
                                    amCallback(reservationIds);
                                }
                            );
                        } else {
                            this.setState({
                                reservationInfoIds: reservationIds,
                            });
                        }
                        // If a number of reservations have been refreshed, I would like to reload the info display if it contains any of said reservations
                        // I don't really want to set it to the new list, it could be anything!
                        // Guess info panel would need to pass a callback function up to provide reload regardless of id changes
                        // If I do that, should I even set ids here? On a refresh I'd assume I just want to update what's present.
                        // Okay, guess I might want to show a completely new reservation as well?
                        // See https://timeedit.atlassian.net/browse/DEV-1899
                    },
                },
            ],
        });

        this.registerMacro("app", {
            events: [Macros.Event.CLEAR_SELECTION],
            actions: [
                {
                    key: Macros.Action.CLEAR_SELECTION,
                    action: () => {
                        if (this.state.activityManagerReservationCallback !== null) {
                            this.setState({ activityManagerReservationCallback: null });
                        }
                    },
                },
            ],
        });

        this.registerMacro("app", {
            events: [Macros.Event.SELECT_REQUEST],
            actions: [
                {
                    key: Macros.Action.SET_REQUEST,
                    action: () => {
                        this.focusFirstCalendar();
                    },
                },
            ],
        });

        this.registerMacro(`app`, {
            events: [Macros.Event.SET_EXTERNAL_TEMPLATE_KIND],
            actions: [
                {
                    key: Macros.Action.SET_EXTERNAL_TEMPLATE_KIND,
                    action: (templateKindId, callback) => {
                        const allCalendars = this.getAllChildren();
                        const firstCalendarIndex = allCalendars.findIndex(
                            (calendar) => calendar instanceof Calendar
                        );
                        const calendar = allCalendars[firstCalendarIndex];
                        if (calendar instanceof Calendar) {
                            TimeEdit.State.update(
                                calendar,
                                calendar.setTemplateKind(templateKindId)
                            );
                        }
                        if (callback && _.isFunction(callback)) {
                            callback();
                        }
                    },
                },
            ],
        });

        this.registerMacro("app", {
            events: [Macros.Event.REQUEST_OPERATION],
            actions: [
                {
                    key: Macros.Action.REQUEST_OPERATION,
                    action: ({ operationType, data, callback }) => {
                        if (operationType === REQUEST_TYPES.NEW_OBJECT) {
                            this.showObjectManager(
                                data.types,
                                [],
                                [],
                                false,
                                false,
                                Language.get("cal_selected_new_object"),
                                true,
                                data.fields,
                                callback
                            );
                        }
                        if (operationType === REQUEST_TYPES.EDIT_OBJECT) {
                            this.showObjectManager(
                                data.types,
                                data.objectIds,
                                data.incompleteObjects,
                                false,
                                false,
                                Language.get(`cal_avail_list_show_object_info`),
                                false,
                                data.fields,
                                callback
                            );
                        }
                        if (operationType === REQUEST_TYPES.MISSING_OBJECT) {
                            this.showObjectManager(
                                data.types,
                                [],
                                [],
                                false,
                                false,
                                Language.get("nc_missing_object"),
                                true,
                                data.fields,
                                callback
                            );
                        }
                        if (operationType === OPERATION_TYPES.CREATE_RESERVATION) {
                            this.clearSchedulingTracks();
                            this.addReservationListener(callback);
                        }
                        if (operationType === OPERATION_TYPES.CREATE_OBJECTS) {
                            this.showGroupManager(data, callback);
                        }
                        if (operationType === OPERATION_TYPES.SET_WEEKS) {
                            if (data.tracks) {
                                this.cancelManualReservationOperation(() => {
                                    this.switchToWeekViewAndSetWeeks(data.weeks, () => {
                                        this.setSchedulingTracks(
                                            data.tracks,
                                            data.onScheduleActivity,
                                            data.reservationMode
                                        );
                                    });
                                });
                            } else {
                                this.switchToWeekViewAndSetWeeks(data.weeks);
                            }
                        }
                        if (operationType === OPERATION_TYPES.CANCEL_AM_SCHEDULING) {
                            this.clearSchedulingTracks();
                            this.cancelManualReservationOperation();
                            this.disableTemporarySingleClick();
                        }
                    },
                },
            ],
        });

        this.registerMacro("app", {
            events: [Macros.Event.AM_CLOSED],
            actions: [
                {
                    key: Macros.Action.AM_CLOSED,
                    action: () => {
                        this.clearSchedulingTracks();
                    },
                },
            ],
        });

        this.registerMacro("app", {
            events: [Macros.Event.SET_EXTERNAL_SELECTION],
            actions: [
                {
                    key: Macros.Action.SET_EXTERNAL_SELECTION,
                    action: (reservationData: {
                        begin?: MillenniumDate;
                        focusDate?: MillenniumDate;
                    }) => {
                        if (reservationData.focusDate || reservationData.begin) {
                            const week = (
                                reservationData.focusDate || reservationData.begin
                            )?.getMillenniumWeek(Language.firstDayOfWeek, Language.daysInFirstWeek);
                            if (week) {
                                this.setWeeks([week]);
                            }
                        }
                    },
                },
            ],
        });
    };

    registerMacro = (id, data) => {
        this.setState((state) => ({ macros: state.macros.register(id, data) }));
    };

    fireEvent = (id, event, ...args) => {
        const actions = this.state.macros.getMatchingActions(id, event);
        setTimeout(
            () =>
                actions.forEach((action) => {
                    action.apply(null, args.concat(id));
                }),
            1
        );
    };

    deregisterMacro = (id) => {
        this.setState((state) => ({ macros: state.macros.deregister(id) }));
    };

    addReservationListener = (callback) => {
        this.setState({ activityManagerReservationCallback: callback });
    };

    cancelManualReservationOperation = (callback = _.noop) => {
        const cb = () => {
            this.fireEvent(`app`, Macros.Event.EXTERNAL_SCHEDULING_CANCELLED);
            callback();
        };
        TimeEdit.State.update(
            this.props.selection,
            this.props.selection.immutableSet({ duration: false })
        );
        if (this.state.activityManagerReservationCallback) {
            this.setState({ activityManagerReservationCallback: null }, cb);
        } else {
            cb();
        }
    };

    moveMemberToScratchpad = (
        memberId: number,
        parentId: number,
        label: string,
        callback: (success: boolean) => void
    ) => {
        this.state.scratchpad.addObject(
            new ScratchpadObject(memberId, parentId, SourceType.Membership, 0, label, undefined)
        );
        API.exportObjects([parentId], (objects) => {
            const modObject = objects[0];
            modObject.members = modObject.members?.filter((member) => {
                if (member.object) {
                    return member.object.id !== memberId;
                }
                return member.id !== memberId;
            });
            API.importObjects([modObject], false, undefined, (result) => {
                let error = false;
                let ids: number[] = [];
                result.forEach((status) => {
                    // An omitted result means 0, which is success.
                    if ((status.result ?? 0) < 0) {
                        error = true;
                        Log.error(status.details ?? "", { code: status.result });
                    } else if (status.reference !== undefined) {
                        ids.push(status.reference);
                    }
                });
                if (!error) {
                    this.fireEvent(
                        "app",
                        Macros.Event.RESERVATION_MADE_OR_MODIFIED,
                        [],
                        false,
                        false
                    );
                }
                callback(!error);
            });
        });
    };

    disableTemporarySingleClick = () => {
        if (this.props.user?.allowTemporarySingleClickReservation) {
            TimeEdit.State.update(
                this.props.user,
                this.props.user.immutableSet({
                    allowTemporarySingleClickReservation: false,
                })
            );
        }
    };

    hasOrderList = (): boolean => {
        const allCalendars = this.getAllChildren();
        if (allCalendars.length === 0) {
            return false;
        }
        return allCalendars.some((calendar) => calendar instanceof OrderSearch);
    };

    switchToWeekView = (onFinished?: () => void) => {
        const allCalendars = this.getAllChildren();
        const index = allCalendars.findIndex((calendar) => calendar instanceof Calendar);
        const weekdayIndex = anyCalendarHasWeekdayHeader(allCalendars);
        if (weekdayIndex !== false && weekdayIndex > -1) {
            this.setActiveCalendar(weekdayIndex);
            if (onFinished) {
                onFinished();
            }
            return;
        }

        const calendar = allCalendars[index];
        if (index !== -1 && calendar instanceof Calendar) {
            const header = new WeekdayHeader(TimeConstants.DAYS_PER_WEEK, 0);
            header.showInfo = true;
            TimeEdit.State.update(
                calendar,
                calendar.setHeader(true, header.freeze().setLimits(calendar.limits))
            );
            this.setActiveCalendar(index);
            if (onFinished) {
                onFinished();
            }
        }
    };

    switchToWeekViewAndSetWeeks = (weeks, callback = _.noop) => {
        this.switchToWeekView(() => {
            this.setWeeks(weeks, callback);
        });
    };

    setWeeks = (weeks, callback = _.noop) => {
        const allCalendars = this.getAllChildren();
        const calendar = allCalendars.find((cal) => {
            if (cal instanceof Calendar) {
                const headers = cal.getHeaderTypeMap();
                if (headers.weekday) {
                    return true;
                }
                return false;
            }
            return false;
        });
        if (calendar instanceof Calendar) {
            const providers = calendar.getProviderMap();
            if (providers.weekday) {
                // Completely redundant check? Should we check something else?
                const headers = calendar.getHeaderTypeMap();
                const provider = headers.weekday;
                TimeEdit.State.update(
                    provider,
                    provider.immutableSet({ weeks: _.sortBy(weeks, "_weekWithYear") })
                );
            }
        }
        callback();
    };

    focusFirstCalendar = (publicOnly = true) => {
        const allCalendars = this.getAllChildren();
        let firstPublicIndex = allCalendars.findIndex(
            (calendar) =>
                calendar instanceof Calendar && !calendar.privateSelected && !calendar.readOnly
        );
        if (firstPublicIndex === -1 && !publicOnly) {
            firstPublicIndex = allCalendars.findIndex(
                (calendar) => calendar instanceof Calendar && !calendar.readOnly
            );
        }
        if (firstPublicIndex > -1) {
            this.setActiveCalendar(firstPublicIndex);
        }
    };

    // Thought: "Focus the calendar in which I am already doing work". Currently used only when clicking an order
    focusBestCalendar = () => {
        const allCalendars = this.getAllChildren();
        const bestIndex = allCalendars.findIndex(
            (calendar) =>
                calendar instanceof Calendar &&
                calendar.selectionGroup &&
                calendar.selectionGroup.length > 0
        );
        if (bestIndex > -1) {
            this.setActiveCalendar(bestIndex);
        } else {
            this.focusFirstCalendar();
        }
    };

    updateDimensions = () => {
        this.setState({
            width: window.innerWidth,
            height: window.innerHeight,
        });
    };

    updateWindowTitle(props) {
        let title = `TimeEdit » ${TimeEdit.serverInfo?.database.name}`;
        if (props.view && props.view.id !== 1) {
            title = `${title} » ${props.view.name}`;
        }
        if (document.title !== title) {
            document.title = title;
        }
    }

    hideAboutDialog = () => {
        this.setState({ aboutVisible: false });
    };

    toggleAboutDialog = (supportInfoLines: string[] = [], showSupportText = false) => {
        this.setState({
            aboutVisible: !this.state.aboutVisible,
            supportInfoLines,
            showSupportText,
        });
    };

    handleHideShortcutOverlay = () => {
        this.setState({ shortcutOverlayVisible: false });
    };

    toggleShortcutOverlay = () => {
        this.setState({ shortcutOverlayVisible: !this.state.shortcutOverlayVisible });
    };

    hideCommandBar = () => {
        this.setState({ commandBarVisible: false });
    };

    toggleCommandBar = () => {
        if (!(this.getActiveCalendar() instanceof Calendar)) {
            Log.info(Language.get("nc_activate_calendar_to_use_command_bar"));
            return;
        }
        this.setState({ commandBarVisible: !this.state.commandBarVisible });
    };

    endLockMode = () => {
        if (_prevEscBinding.length > 0) {
            Mousetrap.unbindWithHelp(MOUSETRAP_ESCAPE);
            if (_prevEscBinding[0]) {
                Mousetrap.bindWithHelp(MOUSETRAP_ESCAPE, _prevEscBinding[0]);
                _prevEscBinding = [];
            }
        }
        this.setState({
            unlockedReservations: null,
            skipConfirmation: false,
            temporaryUnlock: false,
        });
    };

    updateSumTypes = (callback) => {
        API.findSumSettings((result) => {
            API.getSumSetting(result[0], (filters) => {
                this.setState({ filters });
                if (callback) {
                    callback(filters);
                }
            });
        });
    };

    private _floatingWindowCounter = 0;

    // Opens content in a non-modal FloatingWindow. Returns a function that closes
    // the window. Presenting with an already open windowKey replaces that window.
    presentFloatingWindow = (options: TFloatingWindowOptions): (() => void) => {
        const windowKey = options.windowKey ?? `floating-window-${++this._floatingWindowCounter}`;
        this.setState((prevState) => ({
            floatingWindows: [
                ...prevState.floatingWindows.filter((window) => window.windowKey !== windowKey),
                { ...options, windowKey },
            ],
        }));
        return () => this.dismissFloatingWindow(windowKey);
    };

    dismissFloatingWindow = (windowKey: string) => {
        this.setState((prevState) => ({
            floatingWindows: prevState.floatingWindows.filter(
                (window) => window.windowKey !== windowKey
            ),
        }));
    };

    presentModernModal = (
        content: React.JSX.Element,
        displayKey: string | null = null,
        title: string | null = null,
        buttons: any[] | null | "NO_BUTTONS" = null,
        onRemember = _.noop,
        useContentSize = false,
        hasCopyButton = false,
        okButtonTitle = null
    ) => {
        this.presentModal(
            content,
            displayKey,
            title,
            buttons,
            onRemember,
            useContentSize,
            hasCopyButton,
            true,
            okButtonTitle
        );
    };

    presentModal = (
        content: React.JSX.Element,
        displayKey: string | null = null,
        title: string | null = null,
        buttons: any[] | null | "NO_BUTTONS" = null,
        onRemember = _.noop,
        useContentSize = false,
        hasCopyButton = false,
        isModernStyle = false,
        okButtonTitle: string | null = null
    ) => {
        if (!this.state.modalDialogVisible) {
            const dialogProps = {
                content,
                displayKey,
                title,
                buttons,
                onRemember,
                useContentSize,
                hasCopyButton,
                isModernStyle,
                okButtonTitle,
            };
            this.setState({ modalDialogVisible: true, dialogProps });
        } else if (this.state.modalDialogVisible && !this.state.secondModalDialogVisible) {
            const dialog = (
                <ModalDialog
                    hasCopyButton={hasCopyButton}
                    onClose={this.handleDismissSecondModal}
                    displayKey={displayKey}
                    title={title}
                    buttons={buttons}
                    onRemember={onRemember}
                    isModernStyle={isModernStyle}
                    okButtonTitle={okButtonTitle}
                >
                    {content}
                </ModalDialog>
            );
            this.setState({ secondModalDialogVisible: true, secondModalDialog: dialog });
        }
    };

    changeCalendar = (numSteps) => {
        const currentIndex = this.state.activeCalendar;
        let newIndex = currentIndex + numSteps;
        const numCalendars = this.getAllChildren().length;
        if (newIndex < 0) {
            newIndex = numCalendars - 1;
        }
        if (newIndex >= numCalendars) {
            newIndex = 0;
        }
        this.handleActiveCalendarChange(newIndex);
    };

    setupHelp = () => {
        const options = {
            timeout: 0,
            shouldShowTutorial: (viewedHelp) =>
                !viewedHelp.basicTutorial &&
                this.state.showSelection &&
                this.state.activeLayer === 0,
        };
        Tutorial.show(
            "basicTutorial",
            Tutorials.getWelcomeSteps(this.state.menuPosition === MenuModel.POSITION.RIGHT),
            options
        );
    };

    openStaticReservationListAdd = (reservationIds: number[], add: boolean): void => {
        const section = this.props.view.section;
        if (!section) {
            return;
        }
        const name = Language.get("nc_my_list");
        if (section.second instanceof SectionModel && section.second.hasStaticReservations()) {
            const ids = add
                ? _.union(section.second.first.reservationIds, reservationIds)
                : _.difference(section.second.first.reservationIds, reservationIds);
            this.openStaticReservationList(false, ids, name);
            return;
        }

        this.openStaticReservationList(false, reservationIds, name);
    };

    openStaticReservationList = (horizontally: boolean, reservationIds: number[], name: string) => {
        const section = this.props.view.section;
        if (!section) {
            return;
        }
        const limits = section.firstComponent.limits;
        if (section.second instanceof SectionModel && section.second.hasStaticReservations()) {
            const newSection = section.immutableSet({
                second: new SectionModel(
                    new StaticReservations(limits, null, reservationIds, name),
                    null,
                    null,
                    null
                ),
            });
            TimeEdit.State.update(section, newSection);
        } else if (section.second) {
            const DEFAULT_WEIGHT = 0.5;
            const newSection = new SectionModel(
                section,
                new SectionModel(
                    new StaticReservations(limits, null, reservationIds, name),
                    null,
                    null,
                    null
                ),
                DEFAULT_WEIGHT,
                horizontally
            );
            TimeEdit.State.update(section, newSection);
        } else {
            const splitSection = section.split(horizontally, true).immutableSet({
                second: new SectionModel(
                    new StaticReservations(limits, null, reservationIds, name),
                    null,
                    null,
                    null
                ),
            });
            TimeEdit.State.update(section, splitSection);
        }
    };

    toggleReservationList = (horizontally) => {
        let splitToList = true;
        const section = this.props.view.section;
        if (!section) {
            return;
        }
        if (section.second instanceof SectionModel && section.second.hasReservationList()) {
            splitToList = false;
        }
        if (splitToList) {
            if (section.second) {
                const DEFAULT_WEIGHT = 0.5;
                const newSection = new SectionModel(
                    section,
                    new SectionModel(
                        new ReservationSearch(section.firstComponent.limits),
                        null,
                        null,
                        null
                    ),
                    DEFAULT_WEIGHT,
                    horizontally
                );
                TimeEdit.State.update(section, newSection);
            } else {
                TimeEdit.State.update(section, section.split(horizontally, true));
            }
        } else {
            TimeEdit.State.update(section, section.close(false));
        }
    };

    toggleExamComponent = (horizontally = false) => {
        let splitToList = true;
        const section = this.props.view.section;
        if (!section) {
            return;
        }
        if (section.second instanceof SectionModel && section.second.hasExamComponent()) {
            splitToList = false;
        }
        if (splitToList) {
            if (section.second) {
                const DEFAULT_WEIGHT = 0.5;
                const newSection = new SectionModel(
                    section,
                    new SectionModel(new ExamData(section.firstComponent.limits), null, null, null),
                    DEFAULT_WEIGHT,
                    horizontally
                );
                TimeEdit.State.update(section, newSection);
            } else {
                TimeEdit.State.update(
                    section,
                    section.splitToComponent(
                        horizontally,
                        new ExamData(section.firstComponent.limits)
                    )
                );
            }
        } else {
            TimeEdit.State.update(section, section.close(false));
        }
    };

    toggleActivityManager = (horizontally = false) => {
        let splitToList = true;
        const section = this.props.view.section;
        if (!section) {
            return;
        }
        if (section.second instanceof SectionModel && section.second.hasPrefsComponent()) {
            splitToList = false;
        }
        if (splitToList) {
            if (section.second) {
                const DEFAULT_WEIGHT = 0.5;
                const newSection = new SectionModel(
                    section,
                    new SectionModel(
                        new PrefsData(section.firstComponent.limits),
                        null,
                        null,
                        null
                    ),
                    DEFAULT_WEIGHT,
                    horizontally
                );
                TimeEdit.State.update(section, newSection);
            } else {
                TimeEdit.State.update(
                    section,
                    section.splitToComponent(
                        horizontally,
                        new PrefsData(section.firstComponent.limits)
                    )
                );
            }
        } else {
            TimeEdit.State.update(section, section.close(false));
        }
    };

    showObjectManager = (
        types: number[],
        objectIds: number[],
        incompleteObjects: number[],
        createCopy: boolean,
        viewOnly: boolean,
        title: string,
        isNewObject = false,
        newFieldValues = [],
        callback = _.noop
    ) => {
        // Presenting always replaces a currently open editor window without
        // running its close check, matching the previous re-show behavior.
        if (this._closeObjectManagerWindow) {
            this._closeObjectManagerWindow();
            this._closeObjectManagerWindow = null;
            this._objectManagerCloseCheck = undefined;
        }

        let sortOrder = this.state.multiPanelOrder;
        if (viewOnly) {
            sortOrder = pushToFront(sortOrder, "object");
        }
        this.setState({
            types,
            objectIds,
            multiPanelOrder: sortOrder,
            objectRequestCallback: callback,
        });

        if (viewOnly) {
            // Show in side panel if view only
            this._previousEscBinding = Mousetrap.unbindWithHelp(MOUSETRAP_ESCAPE, true)[0];
            TimeEdit.State.replace(this.props.menu, this.props.menu.open("info"));
            Mousetrap.bindWithHelp(MOUSETRAP_ESCAPE, () => {
                TimeEdit.State.replace(this.props.menu, this.props.menu.close());
            });
            return;
        }

        const OBJECT_EDITOR_WIDTH_DEDUCTION = 400;
        const OBJECT_EDITOR_HEIGHT_DEDUCTION = 200;
        const OBJECT_EDITOR_MIN_WIDTH = 600;
        const OBJECT_EDITOR_MIN_HEIGHT = 400;
        this._closeObjectManagerWindow = this.presentFloatingWindow({
            icon: (
                <Icon
                    id="FormOutlined"
                    style={{ fontSize: TOPBAR_BUTTON_ICON_HEIGHT }}
                    alt={title}
                />
            ),
            title,
            onClose: this.handleHideObjectManager,
            initialY: 100,
            initialWidth: Math.max(
                OBJECT_EDITOR_MIN_WIDTH,
                this.state.width - OBJECT_EDITOR_WIDTH_DEDUCTION
            ),
            initialHeight: Math.max(
                OBJECT_EDITOR_MIN_HEIGHT,
                this.state.height - OBJECT_EDITOR_HEIGHT_DEDUCTION
            ),
            minWidth: OBJECT_EDITOR_MIN_WIDTH,
            minHeight: OBJECT_EDITOR_MIN_HEIGHT,
            showCloseButton: false,
            content: (
                <ObjectDefinition
                    flags={this.props.flags}
                    useCreateLabel={title === Language.get("cal_selected_copy_object")}
                    viewOnly={false}
                    types={types}
                    objectIds={objectIds}
                    incompleteObjects={incompleteObjects}
                    createCopy={createCopy}
                    isNewObject={isNewObject}
                    onCancel={this.handleHideObjectManager}
                    onSave={this.handleObjectsSaved}
                    setOnCloseCheck={this.setObjectManagerCloseCheck}
                    newFieldValues={newFieldValues}
                />
            ),
        });
    };

    handleHideObjectManager = (event?, invokeCallback = true) => {
        if (this._objectManagerCloseCheck && invokeCallback) {
            if (!this._objectManagerCloseCheck()) {
                return false;
            }
        }
        this._closeObjectManagerWindow?.();
        this._closeObjectManagerWindow = null;
        this._objectManagerCloseCheck = undefined;

        const callback = this.state.objectRequestCallback;
        this.setState({ objectRequestCallback: null });
        if (callback) {
            callback(null);
        }
        return true;
    };

    showGroupManager = (data, callback) => {
        const horizontally = false;
        let splitToList = true;
        const section = this.props.view.section;
        if (!section) {
            return;
        }
        const allSections = this.getAllChildren();
        const existingManager = _.find(allSections, (sec) => sec instanceof MemberData);
        if (existingManager) {
            splitToList = false;
            const sectionToReplace = section.getCalendarSection(existingManager);
            // Update existing group manager with new props
            TimeEdit.State.update(
                sectionToReplace,
                sectionToReplace.setComponent(
                    new MemberData(
                        sectionToReplace.firstComponent.limits,
                        null,
                        [],
                        data.groupRequests,
                        [],
                        callback
                    )
                )
            );
        }
        if (splitToList) {
            if (section.second) {
                const DEFAULT_WEIGHT = 0.5;
                const newSection = new SectionModel(
                    section,
                    new SectionModel(
                        new MemberData(
                            section.firstComponent.limits,
                            null,
                            [],
                            data.groupRequests,
                            [],
                            callback
                        ),
                        null,
                        null,
                        null
                    ),
                    DEFAULT_WEIGHT,
                    horizontally
                );
                TimeEdit.State.update(section, newSection);
            } else {
                TimeEdit.State.update(
                    section,
                    section.splitToComponent(
                        horizontally,
                        new MemberData(
                            section.firstComponent.limits,
                            null,
                            [],
                            data.groupRequests,
                            [],
                            callback
                        )
                    )
                );
            }
        }
    };

    addReloadFunction = (reloadFunction: () => void) => {
        this.reloadFunctions.push(reloadFunction);
    };

    handleCalendarSwap = (from: number, to: number) => {
        const section = this.props.view.section;
        if (!section) {
            return;
        }
        const calendars = this.getAllChildren();
        const fromSection = section.getCalendarSection(calendars[from]);
        const toSection = section.getCalendarSection(calendars[to]);
        const toCalendar = calendars[to];
        if (!fromSection || !toSection) {
            return;
        }
        TimeEdit.State.update(toSection, toSection.setComponent(calendars[from]));
        TimeEdit.State.update(fromSection, fromSection.setComponent(toCalendar));
        this.setActiveCalendar(to);
    };

    getCalendarIndex = (calendar) => {
        let index = -1;
        this.getAllChildren().some((item, i) => {
            if (item === calendar) {
                index = i;
                return true;
            }
            return false;
        });
        return index;
    };

    /**
     * Every component the view holds. A view with no section has none, which is
     * where it starts before one has loaded.
     */
    getAllChildren = (): TComponentType[] => this.props.view.section?.getAllChildren() ?? [];

    getActiveCalendar = () => {
        const activeId = this.state.activeCalendar;
        let calendar;
        this.getAllChildren().forEach((child, i) => {
            if (i === activeId) {
                calendar = child;
            }
        });
        return calendar;
    };

    handleUpdateObjectSearcher = (objectSearch: ObjectSearch) => {
        this.setState({ objectSearch });
    };

    getObjectSearch = () => {
        return this.state.objectSearch;
    };

    getCurrentFluffyItem = () => {
        return this.state.currentFluffyItem;
    };

    handleEnableDrop = (
        event: (React.MouseEvent | React.DragEvent) & {
            dataTransfer: DataTransfer;
        }
    ) => {
        if (!_.isEventDragDataOfType(event, ["application/x-timeedit-menu"])) {
            return;
        }

        event.preventDefault();
        const POSITION_DIVISOR = 2;

        if (
            event.clientX > this.state.width / POSITION_DIVISOR &&
            this.state.menuPosition !== MenuModel.POSITION.RIGHT
        ) {
            this.setState({ menuPosition: MenuModel.POSITION.RIGHT });
            return;
        }
        if (
            event.clientX < this.state.width / POSITION_DIVISOR &&
            this.state.menuPosition !== MenuModel.POSITION.LEFT
        ) {
            this.setState({ menuPosition: MenuModel.POSITION.LEFT });
            return;
        }
    };

    updateCopiedEntry = (entry: Entry, groupedEntries: Entry[] = []) => {
        this.setState({
            copiedEntry: entry,
            copiedGroupEntries: groupedEntries,
        });
    };

    onModalDragStart = (event: React.DragEvent<HTMLDivElement>) => {
        const dialog = document.getElementsByClassName("draggableDialog")[0] as HTMLElement;
        if (!dialog) {
            return;
        }
        this._dragOffsetLeft = event.clientX - dialog.offsetLeft;
        this._dragOffsetTop = event.clientY - dialog.offsetTop;
    };

    onModalDragOver(event: React.DragEvent<HTMLDivElement>) {
        event.preventDefault();
    }

    onModalDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        const left = event.clientX - this._dragOffsetLeft!;
        const top = event.clientY - this._dragOffsetTop!;
        this.setState({
            dialogPosition: "absolute",
            dialogLeft: `${left}px`,
            dialogTop: `${top}px`,
        });
    };

    toggleBackgroundText = () => {
        this.setState({ skipBackgroundText: !this.state.skipBackgroundText });
    };

    getActiveLayerTitle = () => {
        const layer = _.find(this.state.layers, (lr) => lr.id === this.state.activeLayer);
        if (layer) {
            return layer.name;
        }
        return "";
    };

    isActiveLayerMovingReservations = () => {
        return this.moveLayers ? this.moveLayers[this.state.activeLayer] || false : false;
    };

    isNewUI = () => {
        return this.props.flags?.coreUseNewTeui || false;
    };

    isTEUIStep2 = () => {
        return this.props.flags?.coreUseTeuiStep2 || false;
    };

    render() {
        if (!this.props.token) {
            return null;
        }

        if (!this.state.loadFinished) {
            return null;
        }

        let menuWidth = 55;
        const OPEN_MENU_WIDTH = 300;
        if (this.props.menu.isOpen) {
            menuWidth = menuWidth + OPEN_MENU_WIDTH;
        }

        const getTopBarAdjustment = () => {
            return this.isNewUI() ? 51 : 0;
        };

        const sectionWidth = this.state.width - menuWidth;
        const style: React.CSSProperties = {
            width: sectionWidth,
            height: this.state.height - getTopBarAdjustment(),
        };

        if (this.state.menuPosition === MenuModel.POSITION.RIGHT) {
            style.marginLeft = 0;
        } else {
            style.marginLeft = menuWidth;
        }

        const hasNoView = !this.props.view || !this.props.view.section;

        const MASS_BAR_HEIGHT = 60;
        const selectionHeight = this.state.showSelection
            ? this.state.selectionHeight + getTopBarAdjustment()
            : 0;
        const massChangeBarHeight = this.state.activeLayer !== 0 ? MASS_BAR_HEIGHT : 0;
        const sectionSize = {
            width: sectionWidth,
            height: this.state.height - selectionHeight - massChangeBarHeight,
        };
        const selectionSize = {
            width: sectionWidth,
            height: this.state.selectionHeight,
        };

        const allCalendars = hasNoView ? [] : this.getAllChildren();
        const activeCalendar = allCalendars[this.state.activeCalendar];

        let selectionModel = this.props.selection;
        if (activeCalendar && activeCalendar.privateSelected === true) {
            selectionModel = activeCalendar.selection;
        }

        const messageSize = { width: sectionWidth, marginLeft: style.marginLeft };
        const massChangeBarSize = { width: sectionWidth, height: massChangeBarHeight };

        const rulerClass = ["ruler", "horizontalRuler"];
        if (!this.state.showSelection) {
            rulerClass.push("hidden");
        }

        const rulerStyle = {
            top: this.state.height - this.state.selectionHeight,
        };
        if (this.props.isResizing) {
            rulerStyle.top = this.props.lastMousePosition.clientY;
        }

        let SectionType: typeof Section | typeof TabbedSection = Section;
        const TAB_TRIGGER_HEIGHT = 400;
        const TAB_TOUCH_LIMIT_WIDTH = 600;
        const TAB_TOUCH_LIMIT_HEIGHT = 400;
        let useTabbedSection = this.state.height <= TAB_TRIGGER_HEIGHT; // Touch device and size corresponding to iPad Mini or smaller

        useTabbedSection =
            useTabbedSection ||
            this.state.width <= TAB_TOUCH_LIMIT_WIDTH ||
            this.state.height <= TAB_TOUCH_LIMIT_HEIGHT;
        if (useTabbedSection) {
            SectionType = TabbedSection;
        }

        let aboutDialog = <span />;
        if (this.state.aboutVisible) {
            aboutDialog = (
                <AboutDialog
                    close={this.hideAboutDialog}
                    supportInfoLines={this.state.supportInfoLines}
                    showSupportText={this.state.showSupportText}
                />
            );
        }

        let shortcutOverlay: React.ReactElement | null = null;
        if (this.state.shortcutOverlayVisible) {
            shortcutOverlay = <ShortcutOverlay onClose={this.handleHideShortcutOverlay} />;
        }

        let commandBar = <span />;
        if (this.state.commandBarVisible) {
            const getFluffy = function () {
                return selectionModel.fluffy;
            };
            commandBar = (
                <CommandBar
                    onInfoOpen={this.handleEntryInfoOpen}
                    close={this.hideCommandBar}
                    selection={selectionModel}
                    activeCalendar={this.getActiveCalendar()}
                    getFluffy={getFluffy}
                    hasOrderList={this.hasOrderList}
                />
            );
        }

        let massChangeBar: React.ReactElement | null = null;
        if (this.state.activeLayer !== 0) {
            massChangeBar = this.isTEUIStep2() ? (
                <TeuiMassChangeBar
                    isWorking={this.state.isWorking}
                    progress={this.state.massChangeProgress}
                    style={massChangeBarSize}
                    isModify={this.isActiveLayerMovingReservations()}
                    title={this.getActiveLayerTitle()}
                    performMassChange={this.performExperiment}
                    activeLayer={this.state.activeLayer}
                    layers={this.state.layers}
                    onLeaveDraft={() => this.setActiveLayer(0)}
                    onDeleteDraft={this.cancelExperiment}
                    reloadLayers={this.reloadLayers}
                />
            ) : (
                <MassChangeBar
                    isWorking={this.state.isWorking}
                    progress={this.state.massChangeProgress}
                    style={massChangeBarSize}
                    isModify={this.isActiveLayerMovingReservations()}
                    title={this.getActiveLayerTitle()}
                    performMassChange={this.performExperiment}
                    // cancelMassChange={this.cancelExperiment}
                />
            );
        }

        const reservationIds = this.state.reservationInfoIds;

        let modalDialog: React.ReactElement | null = null;
        if (this.state.modalDialogVisible) {
            const dP = this.state.dialogProps;
            modalDialog = (
                <div
                    className="modalOverlay"
                    onDragOver={this.onModalDragOver}
                    onDrop={this.onModalDrop}
                >
                    {
                        <ModalDialog
                            onDragStart={this.onModalDragStart}
                            style={{
                                top: this.state.dialogTop,
                                left: this.state.dialogLeft,
                                position: this.state.dialogPosition,
                            }}
                            hasCopyButton={dP?.hasCopyButton}
                            onClose={this.handleDismissModal}
                            displayKey={dP?.displayKey}
                            title={dP?.title}
                            buttons={dP?.buttons}
                            onRemember={dP?.onRemember}
                            useContentSize={dP?.useContentSize}
                            isModernStyle={dP?.isModernStyle}
                            okButtonTitle={dP?.okButtonTitle}
                        >
                            {dP?.content}
                        </ModalDialog>
                    }
                </div>
            );
        }

        let secondModalDialog: React.ReactElement | null = null;
        if (this.state.secondModalDialog && this.state.secondModalDialogVisible) {
            secondModalDialog = <div className="modalOverlay">{this.state.secondModalDialog}</div>;
        }

        const user = TimeEdit.State.get()?.user;
        const createIssue = () => {
            const lang = user?.language || getLanguageString();
            const isNordic = _.any(["sv", "nb", "da"], (lg) => lg === lang);
            const url = isNordic
                ? "https://www.timeedit.com/learn"
                : "https://www.timeedit.com/learn";
            window.open(url, this.TARGET);
        };

        const getLanguageString = () => {
            return navigator.languages ? navigator.languages[0] : navigator.language;
        };

        const className = user?.theme;

        const mockOrg = {
            customerSignature: "cloud_timeedit_sso_beta",
            enabledProducts: [EAppId.TE_CORE, EAppId.TE_ADMIN],
            id: "61bf7223c9132785dec5efca",
            name: TimeEdit?.serverInfo?.server.name || "",
            region: ERegion.EU_EES,
            visibility: EOrganizationVisibility.PUBLIC,
        };
        const mockUser: ITEToken = {
            appPermissions: [
                "TE_ADMIN::manageUsers",
                "TE_ADMIN::manageRoles",
                "TE_ADMIN::manageOrganizations",
            ],
            email: "tecore@timeedit.com",
            exp: 4717245107,
            iat: 1685687507,
            id: "63983ac3c65c9400229f018a",
            organizationId: "6407054fddd66a00221641ce",
            organizationsAndRoles: [["656459416cc7746e89ccf496", "admin"]],
            region: ERegion.EU_EES,
            scopes: ["TE_AP", "TE_ADMIN", "TE_CORE", "TE_PREFERENCES"],
            type: ETokenType.USER,
            username: "tecore@timeedit.com",
        };
        const appEnv = (this.props.env?.serverEnv as EAppEnv) || EAppEnv.DEVELOPMENT;

        const appContext = this.getContext();

        return (
            <AppContextProvider context={appContext}>
                <CoreTopBar
                    appEnv={appEnv}
                    user={this.props.token || mockUser}
                    coreUser={user}
                    organization={this.state.organization || mockOrg}
                    createIssue={createIssue}
                    toggleShortcutOverlay={this.toggleShortcutOverlay}
                    toggleAboutDialog={this.toggleAboutDialog}
                    handleViewChange={this.handleViewChange}
                    view={this.props.view}
                    layers={this.state.layers}
                    activeLayer={this.state.activeLayer}
                    setActiveLayer={this.setActiveLayer}
                    onLayerCreated={this.onLayerCreated}
                    onRemoveLayer={this.cancelExperiment}
                    reloadLayers={this.reloadLayers}
                    useTEUIStep2={this.isTEUIStep2()}
                    onLogout={() => {
                        TimeEdit.logoutUser(this.doLogout);
                    }}
                    onAdminClicked={(menuInfo) => {
                        window.open(window.location.href.replace("/client", "/admin"), this.TARGET);
                        menuInfo.domEvent.preventDefault();
                    }}
                    onResetHelpClicked={(menuInfo) => {
                        API.setPreferences("viewedHelp", ["{}"], () => {
                            Log.info(Language.get("nc_client_side_user_menu_help_reset"));
                            this.setupHelp();
                        });
                        menuInfo.domEvent.preventDefault();
                    }}
                />
                <div
                    style={style}
                    className={className}
                    onDragOver={this.handleEnableDrop}
                    onDrop={this.handleDrop}
                >
                    <TempLogListenerSelector messageSize={messageSize} />
                    <Menu
                        useNewUI={this.isNewUI()}
                        useTEUIStep2={this.isTEUIStep2()}
                        flags={this.props.flags}
                        multiPanelOrder={this.state.multiPanelOrder}
                        data={this.props.menu}
                        appHeight={this.state.height - getTopBarAdjustment()}
                        appWidth={this.state.width}
                        activeCalendar={activeCalendar}
                        publicSelection={this.props.selection}
                        showSelection={this.state.showSelection}
                        position={this.state.menuPosition}
                        onSelectionToggle={this.handleToggleSelection}
                        showAbout={this.toggleAboutDialog}
                        showShortcuts={this.toggleShortcutOverlay}
                        onSettingsVisibilityChange={this.handleSetVisibleCalendarIds}
                        onViewChange={this.handleViewChange}
                        view={this.props.view}
                        layers={this.state.layers}
                        activeLayer={this.state.activeLayer}
                        setActiveLayer={this.setActiveLayer}
                        onLayerCreated={this.onLayerCreated}
                        onRemoveLayer={this.cancelExperiment}
                        onPersistLayer={this.performExperiment}
                        reloadLayers={this.reloadLayers}
                        getLayerName={this.getLayerName}
                        getCalendarIndex={this.getCalendarIndex}
                        macroManager={this.state.macros}
                        onDragEnd={this.handleDragEnd}
                        reservationIds={reservationIds}
                        dynamicReservationIds={this.state.dynamicReservationIds}
                        orderIds={this.state.orderInfoIds}
                        isEntryInfo={this.state.isEntryInfo}
                        isInfoEntryEdited={this.state.isInfoEntryEdited}
                        onReservationEditChange={this.onReservationEditChange}
                        onEntryInfoOpen={this.handleEntryInfoOpen}
                        hasAvailableUpdate={this.shouldIndicateUpdate()}
                        resetHelp={this.setupHelp}
                        types={this.state.types}
                        objectIds={this.state.objectIds}
                        onReservationInfoClose={this.onReservationInfoClose}
                        onDynamicReservationInfoClose={this.onDynamicReservationInfoClose}
                        onObjectInfo={this.handleObjectInfo}
                        onObjectInfoClose={this.onObjectInfoClose}
                        onOrderInfoClose={this.onOrderInfoClose}
                    />
                    {massChangeBar}
                    <ViewErrorBoundary size={sectionSize} id={hasNoView ? 0 : this.props.view.id}>
                        <SectionType
                            key={hasNoView ? 0 : this.props.view.id}
                            data={hasNoView ? null : this.props.view.section}
                            flags={this.props.flags}
                            size={sectionSize}
                            selection={this.props.selection}
                            activeLayer={this.state.activeLayer}
                            massChangeEnabled={this.state.massChangeEnabled}
                            filters={this.state.filters}
                            updateSumTypes={this.updateSumTypes}
                            isMassChangeCopy={!this.isActiveLayerMovingReservations()}
                            setActiveLayer={this.setActiveLayer}
                            onLayerCreated={this.onLayerCreated}
                            getLayerName={this.getLayerName}
                            layers={this.state.layers}
                            showCalendarIds={this.state.showCalendarIds}
                            toggleCommandBar={this.toggleCommandBar}
                            activeCalendarId={this.state.activeCalendar}
                            onActiveCalendarChange={this.handleActiveCalendarChange}
                            getCalendarIndex={this.getCalendarIndex}
                            onCalendarSwap={this.handleCalendarSwap}
                            getCurrentFluffyItem={this.getCurrentFluffyItem}
                            getObjectSearch={this.getObjectSearch}
                            onEntryInfoOpen={this.handleEntryInfoOpen}
                            onEditReservationExceptions={this.handleEditReservationExceptions}
                            onOrderInfoOpen={this.handleOrderInfoOpen}
                            onDynamicReservationIdsChanged={this.onDynamicReservationIdsChanged}
                            infoEntryReservationIds={reservationIds}
                            copiedEntry={this.state.copiedEntry}
                            copiedGroupedEntries={this.state.copiedGroupEntries}
                            onEntryCopy={this.updateCopiedEntry}
                            unlockedReservations={this.state.unlockedReservations}
                            temporaryUnlock={this.state.temporaryUnlock}
                            endLockMode={this.endLockMode}
                            skipConfirmation={this.state.skipConfirmation}
                            openStaticReservationList={this.openStaticReservationList}
                            openStaticReservationListAdd={this.openStaticReservationListAdd}
                            skipBackgroundText={this.state.skipBackgroundText}
                            examIdField={this.state.examIdField}
                            setWeeks={this.setWeeks}
                            onObjectInfo={this.handleObjectInfo}
                            hasOrderList={this.hasOrderList}
                            currentTrack={this.state.currentTrack}
                            moveMemberToScratchpad={this.moveMemberToScratchpad}
                        />
                    </ViewErrorBoundary>
                    <div
                        className={rulerClass.join(" ")}
                        style={rulerStyle}
                        onTouchStart={this.props.onResizeStart}
                        onMouseDown={this.props.onResizeStart}
                    />
                    {this.props.user ? (
                        <SelectionPane
                            flags={this.props.flags}
                            getObjectSearch={this.getObjectSearch}
                            size={selectionSize}
                            data={selectionModel}
                            onObjectSearcherChange={this.handleUpdateObjectSearcher}
                            activeCalendar={activeCalendar}
                            activeLayer={this.state.activeLayer}
                            isVisible={this.state.showSelection}
                            onNewObject={this.handleNewObject}
                            onFluffyItemChanged={this.handleFluffyItemChanged}
                            addReloadFunction={this.addReloadFunction}
                            onObjectInfo={this.handleObjectInfo}
                            onEntryInfoOpen={this.handleEntryInfoOpen}
                            unlockedReservations={this.state.unlockedReservations}
                            temporaryUnlock={this.state.temporaryUnlock}
                            skipConfirmation={this.state.skipConfirmation}
                            endLockMode={this.endLockMode}
                            user={this.props.user}
                            schedulingTracks={this.state.schedulingTracks}
                            mappedResult={this.state.mappedResult}
                            onCancelActivityScheduling={() => {
                                this.clearSchedulingTracks();
                            }}
                            currentTrack={this.state.currentTrack}
                            onCurrentTrackChanged={this.onCurrentTrackChanged}
                            hasOrderList={this.hasOrderList}
                        />
                    ) : null}

                    {aboutDialog}
                    {shortcutOverlay}
                    {commandBar}
                    {modalDialog}
                    {secondModalDialog}
                    {this.state.floatingWindows.map(
                        ({ windowKey, content, onClose, ...windowProps }) => (
                            <FloatingWindow
                                key={windowKey}
                                {...windowProps}
                                onClose={() => {
                                    if (onClose?.() === false) {
                                        return;
                                    }
                                    this.dismissFloatingWindow(windowKey);
                                }}
                            >
                                {content}
                            </FloatingWindow>
                        )
                    )}
                </div>
            </AppContextProvider>
        );
    }
}

export default withLDConsumer()(ResizeableComponent.wrap(withAuthContext(App)));
